import React, { useEffect, useMemo, useState, useRef } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api, ROLE_LABELS } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useNotifications } from "@/lib/notifications";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { FormFillerDialog } from "@/components/FormFillerDialog";
import { FilePreviewModal } from "@/components/FilePreviewModal";
import { ReassignDialog, AdminRevokeDialog } from "@/components/PracticeActionDialogs";
import PropertyDocumentsSection from "@/components/PropertyDocumentsSection";
import { RequestDocumentsDialog } from "@/components/RequestDocumentsDialog";
import { SendForSignatureDialog } from "@/components/SendForSignatureDialog";
import { IntegrationRequestModal } from "@/components/IntegrationRequestModal";
import RequestSiteVisitDialog from "@/components/RequestSiteVisitDialog";
import PracticeCustomActionsPanel from "@/components/PracticeCustomActionsPanel";
import PersonalDocDeadlines from "@/components/PersonalDocDeadlines";
import PaymentMethodPickerModal from "@/components/PaymentMethodPickerModal";
import { PriceEditDialog } from "@/components/PriceEditDialog";
import { SatisfactionWidget } from "@/components/SatisfactionWidget";
import { VoiceRecorderButton } from "@/components/VoiceRecorderButton";
import {
  ArrowLeft, PaperPlaneTilt, UploadSimple, FileText, MapPin, User, Clock, ArrowRight, ArrowsClockwise,
  CheckCircle, Kanban as KanbanIcon, FileArrowUp, Trash, Bank, Receipt, CreditCard,
  UserSwitch, Warning, ChatCircleDots, EnvelopeSimple, CalendarBlank, Check, X,
  Phone as PhoneIcon, IdentificationBadge, Briefcase, ShieldStar, PenNib, ShareNetwork,
  CaretDown, CaretRight, CurrencyEur, PlayCircle,
} from "@phosphor-icons/react";
import { shareFile } from "@/lib/shareFile";
import { isFeeLinkExpiredUnpaid, FEE_LINK_TTL_DAYS } from "@/lib/feeExpiry";
import { messageDisplayText } from "@/lib/chatMessageText";
import { PracticeChangeDialog } from "@/components/PracticeChangeDialog";
import { PracticeExtraActions } from "@/components/PracticeExtraActions";
import { RecipientPicker } from "@/components/RecipientPicker";

/**
 * ContactInfoCard (iter52) — Blocco "Dati di Contatto" mostrato in cima alla
 * PracticeDetail. Visibile a admin/supervisor + operatori assegnati (attuali o
 * in coda step_assignments). Non visibile al PRO owner (i suoi contatti sono
 * gestiti dal proprio Profilo).
 *
 * Props:
 *   contactInfo: { professional: {name,email,phone}, client: {name,email,phone} }
 *   canView:     bool (server-side check ridondato lato client per UX)
 */
function ContactInfoCard({ contactInfo, canView }) {
  if (!canView || !contactInfo) return null;
  const { professional = {}, client = {} } = contactInfo;
  const hasProf = professional.name || professional.email || professional.phone;
  const hasClient = client.name || client.email || client.phone;
  if (!hasProf && !hasClient) return null;

  const telHref = (phone) => `tel:${(phone || "").replace(/\s+/g, "")}`;
  const mailHref = (email) => `mailto:${(email || "").trim()}`;

  const Row = ({ icon: Icon, label, value, href, testId }) => (
    <div className="flex items-center gap-2 text-sm py-0.5">
      <Icon size={14} className="text-slate-400 shrink-0" weight="bold" />
      <span className="text-slate-500 text-xs uppercase tracking-wider w-14 shrink-0">{label}</span>
      {value ? (
        href ? (
          <a
            href={href}
            className="text-purple-700 hover:underline font-medium truncate"
            data-testid={testId}
          >
            {value}
          </a>
        ) : (
          <span className="text-slate-800 font-medium truncate" data-testid={testId}>{value}</span>
        )
      ) : (
        <span className="text-slate-400 italic">—</span>
      )}
    </div>
  );

  const Block = ({ eyebrow, name, email, phone, roleColor, roleIcon: RoleIcon, testId }) => (
    <div className="flex-1 min-w-[240px] rounded-lg border bg-slate-50/60 p-3" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <div
          className="w-7 h-7 rounded-md flex items-center justify-center shrink-0"
          style={{ background: `${roleColor}18`, color: roleColor }}
        >
          <RoleIcon size={14} weight="fill" />
        </div>
        <div className="label-eyebrow text-[10px]">{eyebrow}</div>
      </div>
      <Row icon={User} label="Nome" value={name} testId={`${testId}-name`} />
      <Row icon={PhoneIcon} label="Tel" value={phone} href={phone ? telHref(phone) : null} testId={`${testId}-phone`} />
      <Row icon={EnvelopeSimple} label="Email" value={email} href={email ? mailHref(email) : null} testId={`${testId}-email`} />
    </div>
  );

  return (
    <div className="kpi-card mb-4 p-4" data-testid="practice-contact-info">
      <div className="flex items-center gap-2 mb-3">
        <IdentificationBadge size={16} className="text-purple-700" weight="duotone" />
        <div className="font-semibold text-sm text-slate-800">Dati di Contatto</div>
        <div className="ml-auto text-xs text-muted-foreground italic">click su telefono/email per chiamare o scrivere</div>
      </div>
      <div className="flex gap-3 flex-wrap">
        {hasProf && (
          <Block
            eyebrow="Professionista"
            name={professional.name}
            email={professional.email}
            phone={professional.phone}
            roleColor="#0369A1"
            roleIcon={Briefcase}
            testId="contact-block-pro"
          />
        )}
        {hasClient && (
          <Block
            eyebrow="Cliente / Proprietario"
            name={client.name}
            email={client.email}
            phone={client.phone}
            roleColor="#166534"
            roleIcon={User}
            testId="contact-block-client"
          />
        )}
      </div>
    </div>
  );
}


const fmt = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);
const fmtDate = (iso) => new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

/**
 * Iter208.9 — Traduzione azioni History in italiano leggibile.
 * Restituisce { verb, target } — `verb` è il predicato ("ha avanzato"),
 * `target` è ciò su cui l'azione è stata compiuta ("allo step Sopralluogo").
 */
const HISTORY_VERBS = {
  created: "ha creato",
  updated: "ha aggiornato",
  accepted: "ha accettato",
  completed: "ha completato",
  advanced: "ha avanzato",
  reassigned: "ha riassegnato",
  step_reassigned: "ha riassegnato lo step",
  revived: "ha ripristinato",
  rejected: "ha rifiutato",
  revoked: "ha revocato",
  cancelled: "ha annullato",
  rilavora: "ha richiesto una rilavorazione",
  payment_override: "ha sbloccato senza pagamento",
  sent_back: "ha rimandato indietro",
  advance_paid: "ha registrato l'anticipo",
  payment_received: "ha registrato il saldo",
  delivered_frozen: "ha consegnato (congelata)",
  delivered: "ha consegnato",
  archived_without_delivery: "ha archiviato senza consegna",
  documents_cleanup_on_cancel: "ha ripulito i documenti",
  documents_requested: "ha richiesto documenti",
  visit_rescheduled: "ha riprogrammato il sopralluogo",
  site_visit_step_inserted: "ha aggiunto un sopralluogo",
  solicited: "ha sollecitato",
  integration_requested_by_pro: "ha richiesto un'integrazione",
};

function formatHistoryEntry(h) {
  const by = h.by_name || h.by_email || "Sistema";
  const action = (h.action || "").toString();
  const verb = HISTORY_VERBS[action] || `ha eseguito "${action.replace(/_/g, " ")}"`;
  let target = "";
  if (h.step) target = ` allo step ${h.step}`;
  else if (action === "reassigned" && h.to_name) target = ` la pratica a ${h.to_name}`;
  else if (action === "step_reassigned" && h.assigned_to_name) target = ` a ${h.assigned_to_name}`;
  else if (action === "revived" && h.assigned_to_name) target = `, assegnata a ${h.assigned_to_name}`;
  else if (action === "created") target = " la pratica";
  return { by, verb, target };
}


const ROLE_COLORS = {
  admin: "#991B1B",
  desk_operator: "#4F46E5",
  site_operator: "#166534",
  tech_operator: "#CA8A04",
  accounting_operator: "#6D28D9",
  professional: "#0369A1",
};

/**
 * VisitHistoryTimeline (Iter68) — Timeline dei sopralluoghi effettuati
 * e pianificati sulla pratica (primario + secondari).
 *
 * Consuma `GET /practices/{pid}/visit-history`. Visibile a partecipanti,
 * admin e PRO owner (l'endpoint applica l'auth). Se non ci sono sopralluoghi
 * il componente non renderizza nulla.
 */
function VisitHistoryTimeline({ practiceId }) {
  const [visits, setVisits] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get(`/practices/${practiceId}/visit-history`)
      .then(r => { if (!cancelled) setVisits(r.data?.visits || []); })
      .catch(() => { if (!cancelled) setVisits([]); });
    return () => { cancelled = true; };
  }, [practiceId]);

  if (!visits || visits.length === 0) return null;

  const STATUS_META = {
    completed: { label: "Completato", cls: "bg-green-100 text-green-800 border-green-200", dot: "bg-green-500" },
    current:   { label: "In corso",   cls: "bg-blue-100 text-blue-800 border-blue-200",   dot: "bg-blue-500 ring-4 ring-blue-100" },
    pending:   { label: "In programma", cls: "bg-slate-100 text-slate-700 border-slate-200", dot: "bg-slate-400" },
    cancelled: { label: "ANNULLATA",  cls: "bg-red-100 text-red-700 border-red-300 font-black uppercase tracking-wider",     dot: "bg-red-500" },
  };

  return (
    <div className="kpi-card" data-testid="visit-history-timeline">
      <div className="flex items-center gap-2 mb-4">
        <MapPin size={18} weight="fill" className="text-purple-600" />
        <h3 className="font-display text-lg font-semibold">Storico Sopralluoghi</h3>
        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-purple-100 text-purple-800 font-semibold">
          {visits.length} {visits.length === 1 ? "sopralluogo" : "sopralluoghi"}
        </span>
      </div>

      <ol className="relative border-l-2 border-slate-200 ml-2 space-y-5">
        {visits.map((v) => {
          const meta = STATUS_META[v.status] || STATUS_META.pending;
          const op = v.operator;
          const req = v.requested_by;
          return (
            <li key={v.step_id} className="pl-6 relative" data-testid={`visit-item-${v.step_id}`}>
              <span
                className={`absolute -left-[9px] top-1.5 w-4 h-4 rounded-full border-2 border-white ${meta.dot}`}
                aria-hidden
              />
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-semibold border ${v.is_primary ? "bg-indigo-100 text-indigo-800 border-indigo-200" : "bg-amber-100 text-amber-800 border-amber-200"}`}>
                  {v.is_primary ? "Primario" : "Secondario"}
                </span>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-semibold border ${meta.cls}`}>
                  {meta.label}
                </span>
                {v.duration_minutes && (
                  <span className="text-[11px] text-muted-foreground">
                    <Clock size={11} weight="bold" className="inline mr-0.5" />
                    {v.duration_minutes} min
                  </span>
                )}
              </div>
              <div className="font-medium text-sm mt-1 text-slate-800">
                {v.name || "Sopralluogo"}
              </div>
              <div className="mt-1 text-xs text-muted-foreground space-y-0.5">
                <div>
                  <CalendarBlank size={12} weight="bold" className="inline mr-1 text-slate-400" />
                  {v.scheduled_at
                    ? <span className="text-slate-700 font-medium">{fmtDate(v.scheduled_at)}</span>
                    : <span className="italic">Data da pianificare</span>}
                </div>
                <div>
                  <User size={12} weight="bold" className="inline mr-1 text-slate-400" />
                  {op
                    ? <span>Operatore: <span className="text-slate-700 font-medium">{op.full_name}</span></span>
                    : <span className="italic">Operatore non ancora assegnato</span>}
                </div>
                {req && (
                  <div>
                    <UserSwitch size={12} weight="bold" className="inline mr-1 text-slate-400" />
                    Richiesto da <span className="text-slate-700 font-medium">{req.full_name}</span>
                    {v.requested_at && <> · {fmtDate(v.requested_at)}</>}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/**
 * InternalNotesCard (Iter69) — Note Interne / Istruzioni Admin.
 *
 * Iter89: le note sono ora visibili anche agli operatori assegnati alla
 * pratica (in sola lettura) — non solo ad admin/supervisor. Il PATCH resta
 * ristretto ad admin/supervisor lato backend (silenziosamente scartato per
 * gli altri ruoli). I professionisti continuano a NON vedere queste note.
 */
function InternalNotesCard({ practiceId, initialValue, onSaved, readOnly = false }) {
  const [value, setValue] = useState(initialValue || "");
  const [saving, setSaving] = useState(false);
  // Iter159: quando la nota è vuota e l'admin apre la card, mostriamo una
  // riga cliccabile compatta ("+ Aggiungi note interne") che espande il
  // textarea. Riduce clutter visivo quando non ci sono note.
  const [expanded, setExpanded] = useState(!!(initialValue || "").trim());
  const dirty = value !== (initialValue || "");

  useEffect(() => {
    setValue(initialValue || "");
    setExpanded(!!(initialValue || "").trim());
  }, [initialValue]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/practices/${practiceId}`, { internal_notes: value });
      toast.success("Note interne aggiornate");
      onSaved && onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  // Modalità read-only per operatori: se non ci sono note, non renderizzare.
  if (readOnly && !(initialValue || "").trim()) return null;

  // Iter159: modalità admin collapsed quando non ci sono note ancora.
  if (!readOnly && !expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="w-full flex items-center gap-2 py-3 px-4 rounded-md border border-dashed border-amber-300 bg-amber-50/30 hover:bg-amber-50 transition text-left"
        data-testid="internal-notes-add-btn"
      >
        <ShieldStar size={16} weight="fill" className="text-amber-600" />
        <span className="text-sm font-medium text-amber-900">+ Aggiungi note interne</span>
        <span className="text-[10px] uppercase tracking-wider text-amber-700 ml-auto">Solo Admin</span>
      </button>
    );
  }

  return (
    <div className="kpi-card border-l-4 border-amber-400" data-testid="internal-notes-card">
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <ShieldStar size={18} weight="fill" className="text-amber-600" />
          <h3 className="font-display text-lg font-semibold">Note Interne</h3>
          <span
            className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold"
            data-testid="internal-notes-badge"
          >
            {readOnly ? "Sola lettura" : "Solo Admin"}
          </span>
        </div>
        {!readOnly && (
          <Button
            size="sm"
            onClick={save}
            disabled={!dirty || saving}
            className="ht-gradient text-white"
            data-testid="internal-notes-save"
          >
            {saving ? "Salvataggio…" : "Salva"}
          </Button>
        )}
      </div>
      <p className="text-xs text-muted-foreground mb-2">
        {readOnly
          ? "Istruzioni interne condivise dall'Admin. Modificabili solo da Admin/Supervisor."
          : "Istruzioni riservate ad Admin, Supervisor e operatori assegnati. I professionisti non vedono queste note."}
      </p>
      {readOnly ? (
        <div
          className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed p-3 rounded border border-amber-200 bg-amber-50/40"
          data-testid="internal-notes-readonly"
        >
          {initialValue}
        </div>
      ) : (
        <Textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Aggiungi istruzioni interne, promemoria, note di lavorazione…"
          rows={5}
          className="resize-y bg-amber-50/40 border-amber-200 focus-visible:ring-amber-400"
          data-testid="internal-notes-textarea"
        />
      )}
    </div>
  );
}

export default function PracticeDetail() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { refresh } = useNotifications();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [prop, setProp] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [mentionables, setMentionables] = useState([]);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionIdx, setMentionIdx] = useState(0);
  // Modal richiesta integrazione: si apre quando arrivi da notifica con
  // ?integration_request=<req_id>. Cerca la richiesta in practice.integration_requests.
  const [integrationRequestOpen, setIntegrationRequestOpen] = useState(false);
  const [integrationRequestData, setIntegrationRequestData] = useState(null);
  // Iter124 — Revisione Supervisore
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [supervisorsList, setSupervisorsList] = useState([]);
  const [supervisorLoading, setSupervisorLoading] = useState(false);
  const [pickedSupervisorId, setPickedSupervisorId] = useState("");
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const fileRef = useRef();
  const chatEndRef = useRef();
  const textareaRef = useRef();

  const load = async () => {
    try {
      const r = await api.get(`/practices/${id}`);
      setData(r.data);
      setLoadError(null);
      if (window.location.hash === "#bollettini") {
        setTimeout(() => document.getElementById("bollettini")?.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
      }
      // Fetch full property info per indirizzo completo + proprietario
      if (r.data?.practice?.property_id) {
        api.get(`/properties/${r.data.practice.property_id}`).then(rr => setProp(rr.data?.property || null)).catch(() => setProp(null));
      }
      refresh();
    } catch (err) {
      // Iter165 — Errori chiari invece di "Caricamento..." infinito.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 403) {
        setLoadError({
          code: 403,
          title: "Accesso non consentito",
          message: detail || "Non hai i permessi per visualizzare questo fascicolo.",
        });
      } else if (status === 404) {
        setLoadError({
          code: 404,
          title: "Pratica non trovata",
          message: detail || "La pratica richiesta non esiste o è stata rimossa.",
        });
      } else {
        setLoadError({
          code: status || 0,
          title: "Errore nel caricamento",
          message: detail || "Impossibile caricare la pratica. Riprova più tardi.",
        });
        toast.error(detail || "Errore nel caricamento");
      }
    }
  };

  useEffect(() => { load(); }, [id]);

  // Iter208.92 — Se l'utente è un operatore/admin, marca come lette le
  // comunicazioni non lette di questa pratica (best-effort, non bloccante).
  useEffect(() => {
    if (!id) return;
    if (!user) return;
    const isOperator = user.role !== "professional" && user.role !== "subuser" && user.role !== "collaborator";
    if (!isOperator) return;
    api.post(`/communications/practice/${id}/mark-read-by-operator`).catch(() => {});
  }, [id, user]);
  // Iter149: ricarica dopo assegnazione step (evento emesso da AssignmentFlowPanel)
  useEffect(() => {
    const h = () => load();
    window.addEventListener("practice:reload", h);
    return () => window.removeEventListener("practice:reload", h);
  }, [id]);
  useEffect(() => {
    api.get(`/practices/${id}/mentionable-users`).then(r => setMentionables(r.data || [])).catch(() => setMentionables([]));
  }, [id]);

  // Iter97: auto-mark delle notifiche non lette quando l'utente apre la
  // pagina di dettaglio pratica (destinazione finale delle notifiche di
  // messaggio chat/menzione). Chiamato una sola volta al mount, senza
  // aggressività su cambi pagina — solo se ci sono notifiche pendenti.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const r = await api.get("/notifications/per-practice");
        const n = (r.data?.per_practice || {})[id] || 0;
        if (!cancelled && n > 0) {
          await api.post("/notifications/mark-read", { practice_id: id });
          setUnreadCount(0);
          refresh(); // aggiorna badge sidebar/kanban
        } else if (!cancelled) {
          setUnreadCount(n);
        }
      } catch { /* ignore */ }
    };
    run();
    return () => { cancelled = true; };
  }, [id, refresh]);

  // Polling 30s per aggiornare in tempo reale se arrivano nuove notifiche
  // mentre la pagina è aperta.
  const loadUnread = () => {
    api.get("/notifications/per-practice")
      .then(async r => {
        const n = (r.data?.per_practice || {})[id] || 0;
        if (n > 0) {
          // Auto-mark subito: l'utente è ancora sulla pratica → chat visibile
          try {
            await api.post("/notifications/mark-read", { practice_id: id });
            setUnreadCount(0);
            refresh();
          } catch { setUnreadCount(n); }
        } else {
          setUnreadCount(0);
        }
      })
      .catch(() => {});
  };
  useEffect(() => {
    const t = setInterval(loadUnread, 30000);
    return () => clearInterval(t);
  }, [id]);
  useEffect(() => {
    if (data && chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [data?.messages?.length]);

  // Poll every 15s for new messages
  useEffect(() => {
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [id]);

  // Se URL contiene ?integration_request=<req_id>, cerca la richiesta corrispondente
  // in practice.integration_requests e apri il modal. Rimuovi poi il query param
  // dall'URL per evitare riapertura al refresh.
  useEffect(() => {
    if (!data?.practice) return;
    const reqId = searchParams.get("integration_request");
    if (!reqId) return;
    const requests = data.practice.integration_requests || [];
    const found = requests.find((r) => r.id === reqId);
    setIntegrationRequestData(found || null);
    setIntegrationRequestOpen(true);
    // Rimuovi il param dall'URL (usa replace per non aggiungere a history)
    const next = new URLSearchParams(searchParams);
    next.delete("integration_request");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.practice?.integration_requests]);

  // Detect @ mention trigger in the textarea
  const onMsgChange = (e) => {
    const v = e.target.value;
    setMsg(v);
    const caret = e.target.selectionStart ?? v.length;
    const before = v.slice(0, caret);
    const m = before.match(/@([A-Za-zÀ-ÿ0-9_\.\-]*)$/);
    if (m) {
      setMentionQuery(m[1].toLowerCase());
      setMentionIdx(0);
      setMentionOpen(true);
    } else {
      setMentionOpen(false);
    }
  };

  const filteredMentions = React.useMemo(() => {
    const q = mentionQuery;
    if (!q) return mentionables.slice(0, 8);
    return mentionables.filter(u => {
      const first = (u.first_name || "").toLowerCase();
      const handle = (u.handle || "").toLowerCase();
      const disp = (u.display_name || "").toLowerCase();
      return first.startsWith(q) || handle.startsWith(q) || disp.includes(q);
    }).slice(0, 8);
  }, [mentionables, mentionQuery]);

  const insertMention = (u) => {
    const ta = textareaRef.current;
    const caret = ta?.selectionStart ?? msg.length;
    const before = msg.slice(0, caret);
    const after = msg.slice(caret);
    // Se altri utenti condividono lo stesso first_name usa handle full
    const same = mentionables.filter(x => (x.first_name || "").toLowerCase() === (u.first_name || "").toLowerCase());
    const token = same.length > 1 ? u.handle : ((u.first_name || u.handle).toLowerCase().replace(/\s+/g, ""));
    const newBefore = before.replace(/@([A-Za-zÀ-ÿ0-9_\.\-]*)$/, `@${token} `);
    const nextValue = newBefore + after;
    setMsg(nextValue);
    setMentionOpen(false);
    setTimeout(() => {
      if (ta) {
        ta.focus();
        const pos = newBefore.length;
        ta.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const onMsgKeyDown = (e) => {
    if (mentionOpen && filteredMentions.length > 0) {
      if (e.key === "ArrowDown") { e.preventDefault(); setMentionIdx(i => (i + 1) % filteredMentions.length); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setMentionIdx(i => (i - 1 + filteredMentions.length) % filteredMentions.length); return; }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insertMention(filteredMentions[mentionIdx]);
        return;
      }
      if (e.key === "Escape") { e.preventDefault(); setMentionOpen(false); return; }
    }
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(e); }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!msg.trim()) return;
    setSending(true);
    try {
      await api.post(`/practices/${id}/messages`, { body: msg });
      setMsg("");
      setMentionOpen(false);
      load();
    } catch (err) { toast.error("Errore invio messaggio"); }
    finally { setSending(false); }
  };

  const uploadDoc = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    await uploadDocs(files);
    e.target.value = "";
  };

  // Upload sequenziale di uno o più file. Riutilizzato da input picker e da drag&drop.
  const uploadDocs = async (files) => {
    let ok = 0, fail = 0;
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("category", "operativo");
      try {
        await api.post(`/practices/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok++;
      } catch (err) { fail++; }
    }
    if (ok) toast.success(fail ? `${ok} documento/i caricato/i · ${fail} falliti` : `${ok} documento/i caricato/i`);
    else if (fail) toast.error(`Errore upload (${fail} file)`);
    load();
  };

  const releaseStandby = async () => {
    try {
      await api.post(`/practices/${id}/standby/release`);
      toast.success("Pratica sbloccata · operatore avvisato");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const advance = async () => {
    const pr = data?.practice;
    const adminForce = user.role === "admin" || user.is_supervisor;
    if (adminForce && pr?.current_assigned_to && !window.confirm(
      "Avanzamento forzato: eventuali blocchi verranno annullati e l'operatore corrente verrà rimosso dalla pratica. Nessun operatore sarà assegnato al prossimo step. Continuare?",
    )) return;
    try {
      const r = await api.post(`/practices/${id}/advance`);
      // Iter207.17: fallback su "In elaborazione" quando il backend non
      // ritorna next_step (es. passaggio operatore per sopralluogo secondario
      // dove il prossimo step viene calcolato solo dopo la riassegnazione).
      const nextName = r.data?.next_step || r.data?.current_step_name || "In elaborazione";
      toast.success(r.data.status === "completed" ? "Pratica completata" : `Avanzata a: ${nextName}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  // Consegna lavoro (solo step finale, solo assegnatario)
  const [deliverOpen, setDeliverOpen] = useState(false);
  const [deliverFiles, setDeliverFiles] = useState([]);
  const [delivering, setDelivering] = useState(false);
  // Azioni admin sulla pratica (Riassegna / Annulla)
  const [reassignOpen, setReassignOpen] = useState(false);
  const [changeOpen, setChangeOpen] = useState(false);
  const [adminRevokeOpen, setAdminRevokeOpen] = useState(false);
  const [priceEditOpen, setPriceEditOpen] = useState(false);
  // Iter208.48 — Rimborso pratica (admin)
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [refundMode, setRefundMode] = useState("total"); // "total" | "partial"
  const [refunding, setRefunding] = useState(false);
  const [docReqOpen, setDocReqOpen] = useState(false);
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [siteVisitReqOpen, setSiteVisitReqOpen] = useState(false);
  const [rilavoraOpen, setRilavoraOpen] = useState(false); // Iter104
  const [permDeleteOpen, setPermDeleteOpen] = useState(false); // Iter208.12
  const [permDeleteConfirmText, setPermDeleteConfirmText] = useState(""); // Iter208.12
  const [permDeleting, setPermDeleting] = useState(false); // Iter208.12
  const [rilavoraReason, setRilavoraReason] = useState("");
  const [rilavoraConfirm, setRilavoraConfirm] = useState(false);
  const [rilavoraSubmitting, setRilavoraSubmitting] = useState(false);
  // Iter202 — Archivia senza consegna (solo pratiche rianimate)
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveSubmitting, setArchiveSubmitting] = useState(false);
  const doArchiveWithoutDelivery = async () => {
    setArchiveSubmitting(true);
    try {
      await api.post(`/practices/${id}/archive`);
      toast.success("Pratica archiviata senza consegna");
      setArchiveOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore archiviazione");
    } finally {
      setArchiveSubmitting(false);
    }
  };
  const doRilavora = async () => {
    if (rilavoraReason.trim().length < 10 || !rilavoraConfirm) return;
    setRilavoraSubmitting(true);
    try {
      await api.post(`/practices/${p._id}/rilavora`, { reason: rilavoraReason.trim() });
      toast.success("Pratica riaperta. Operatore notificato.");
      setRilavoraOpen(false);
      setRilavoraReason("");
      setRilavoraConfirm(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setRilavoraSubmitting(false);
    }
  };
  // Previous visit operator: caricato in lazy per proporre tooltip informativo.
  const [previousVisitOp, setPreviousVisitOp] = useState(null);
  useEffect(() => {
    if (!id) return;
    api.get(`/practices/${id}/previous-visit-operator`)
      .then(r => setPreviousVisitOp(r.data?.operator || null))
      .catch(() => setPreviousVisitOp(null));
  }, [id]);
  // Step interno del dialog Consegna: 1 = upload+prima conferma, 2 = conferma definitiva
  const [deliverStep, setDeliverStep] = useState(1);
  const [deliverSendTo, setDeliverSendTo] = useState("pro");
  const doDeliver = async () => {
    if (!deliverFiles.length) { toast.error("Carica almeno un PDF"); return; }
    setDelivering(true);
    try {
      const fd = new FormData();
      deliverFiles.forEach(f => fd.append("files", f));
      fd.append("send_to", deliverSendTo);
      const r = await api.post(`/practices/${id}/deliver`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 10 * 60 * 1000,
      });
      toast.success(`Lavoro consegnato: ${r.data.files_count} file caricati nella cartella "${r.data.folder_name}"${deliverSendTo === "client" ? " · inviati al cliente finale" : ""}`);
      setDeliverOpen(false);
      setDeliverFiles([]);
      setDeliverStep(1);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore consegna");
    } finally { setDelivering(false); }
  };

  // Reset dello step interno ad ogni apertura/chiusura del dialog.
  useEffect(() => {
    if (!deliverOpen) { setDeliverStep(1); setDeliverFiles([]); setDeliverSendTo("pro"); }
  }, [deliverOpen]);

  const sortedSteps = useMemo(
    () => (data?.workflow?.steps || []).slice().sort((a, b) => a.order - b.order),
    [data?.workflow?.steps]
  );

  // Iter208.9 — Chat pratica: escludi i system_action (azioni operative automatiche)
  const chatMessages = useMemo(
    () => (data?.messages || []).filter((m) => m?.kind !== "system_action"),
    [data?.messages]
  );

  // Iter124 — Revisione Supervisore
  const openReviewModal = async () => {
    setReviewModalOpen(true);
    if (supervisorsList.length > 0) return;
    try {
      setSupervisorLoading(true);
      const r = await api.get("/supervisors");
      setSupervisorsList(Array.isArray(r.data) ? r.data : []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossibile caricare i supervisori.");
    } finally {
      setSupervisorLoading(false);
    }
  };
  const submitReviewRequest = async () => {
    if (!pickedSupervisorId) { toast.error("Seleziona un supervisore"); return; }
    try {
      setReviewSubmitting(true);
      await api.post(`/practices/${id}/request-review`, { supervisor_id: pickedSupervisorId });
      toast.success("Revisione richiesta al supervisore.");
      setReviewModalOpen(false);
      setPickedSupervisorId("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nella richiesta di revisione.");
    } finally {
      setReviewSubmitting(false);
    }
  };
  const completeReview = async () => {
    if (!window.confirm("Confermi il completamento della revisione? L'operatore potrà procedere con la consegna.")) return;
    try {
      await api.post(`/practices/${id}/complete-review`);
      toast.success("Revisione completata: consegna sbloccata.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nel completamento della revisione.");
    }
  };

  if (loadError) {
    return (
      <div className="max-w-lg mx-auto mt-16" data-testid="practice-load-error">
        <Link
          to={user?.role === "professional" ? "/dashboard" : "/board"}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"
          data-testid="back-link-error"
        >
          <ArrowLeft size={16} /> {user?.role === "professional" ? "Torna alla dashboard" : "Torna alla bacheca"}
        </Link>
        <div className="kpi-card p-6 text-center">
          <div className="text-5xl mb-3">{loadError.code === 403 ? "🔒" : loadError.code === 404 ? "🔍" : "⚠️"}</div>
          <h2
            className="font-display text-xl font-bold text-slate-900 mb-2"
            data-testid="practice-load-error-title"
          >
            {loadError.title}
          </h2>
          <p className="text-sm text-slate-600" data-testid="practice-load-error-message">
            {loadError.message}
          </p>
        </div>
      </div>
    );
  }
  if (!data) return <div>Caricamento...</div>;
  // Iter163 — Null-guards resilienti: se il backend ritorna una pratica senza
  // service_id (o con l'oggetto servizio completo al posto dell'ID), evitiamo
  // che la pagina crashi. Normalizziamo tutti i campi opzionali via `|| {}` /
  // `|| []` più a valle.
  const p = data.practice || null;
  if (!p) return <div>Pratica non trovata</div>;
  const wf = data.workflow || null;
  const svc = data.service || {};
  // Iter163 — Alcune vecchie pratiche possono avere `service_id` come oggetto
  // (dump del servizio invece del solo ID). Normalizziamo così i figli
  // (SiteVisitCalendar, ecc.) ricevono sempre una stringa.
  const rawSvcId = p.service_id;
  const serviceIdStr = typeof rawSvcId === "string"
    ? rawSvcId
    : (rawSvcId && (rawSvcId._id || rawSvcId.id)) || svc._id || "";
  if (rawSvcId && typeof rawSvcId !== "string") {
    p.service_id = serviceIdStr;
  }
  // Iter163 — Elenco accessori acquistati insieme al primario (rollup nel
  // total_price). Usato nel riepilogo per pratiche tipo "APE + Visura/Scheda".
  const purchasedChained = Array.isArray(p.chained_services)
    ? p.chained_services.filter((cs) => cs && cs.purchased)
    : [];
  const isAdmin = user.role === "admin" || user.is_supervisor;
  const isPro = user.role === "professional";
  // Admin/supervisor deve poter eseguire TUTTE le azioni operatore su qualunque pratica.
  const isAssignee = p.current_assigned_to === user.id;
  const canOperate = isAdmin || isAssignee;
  const canAdvance = isAdmin || isAssignee;
  // Iter110: allineamento con Kanban — se lo stato dell'operatore corrente
  // per QUESTA pratica è "assegnazione" (deve ancora Accettare), nascondiamo
  // le azioni operative (Consegna, Richiedi integrazione, ecc.) e lasciamo
  // solo Accetta / Revoca. Se admin, non nascondiamo nulla.
  const myOperatorState = (p.operator_states || {})[user.id] || null;
  const isAssegnazione = !isAdmin && isAssignee && myOperatorState === "assegnazione";
  const canSendSignature = !!p && p.status !== "completed" && p.status !== "cancelled"
    && user.role !== "professional" && !isAssegnazione
    && (isAdmin || p.current_assigned_to === user.id || Object.values(p.step_assignments || {}).includes(user.id));

  // Iter166 — Rimossa la supervisione globale legacy a livello di servizio.
  // L'unico switch valido è ora `requires_supervision` sullo step corrente
  // (workflow snapshot). Se lo step non richiede supervisione, avanza/consegna
  // sono sempre immediatamente disponibili — nessun banner giallo.
  const currentStepSnapshot = (p.workflow_steps_snapshot || []).find(
    (s) => s?.id === p.current_step_id,
  ) || null;
  const stepRequiresSupervision = !!currentStepSnapshot?.requires_supervision;
  const reviewEnabled = stepRequiresSupervision;
  const currentStepReview = stepRequiresSupervision
    ? ((p.step_reviews || {})[p.current_step_id] || null)
    : null;
  const reviewRequested = !!currentStepReview?.requested;
  const reviewCompleted = !!currentStepReview?.completed;
  const assignedSupervisorId = currentStepReview?.supervisor_id || null;
  const isAssignedSupervisor = reviewRequested && assignedSupervisorId === user.id;
  const isPracticeParticipant = isAssignee || Object.values(p.step_assignments || {}).includes(user.id);
  // Iter145: admin e pro NON possono richiedere la revisione — solo l'operatore
  // che sta effettivamente lavorando lo step corrente (assegnatario corrente
  // oppure partecipante alla cascata degli step).
  const canRequestReview =
    reviewEnabled &&
    !reviewCompleted &&
    !reviewRequested &&
    !isPro &&
    !isAdmin &&
    isPracticeParticipant;
  const deliverBlocked = reviewEnabled && !reviewCompleted;
  const assignedSupName = (() => {
    if (!reviewRequested) return "";
    const uid = assignedSupervisorId;
    const ops = p.assigned_operators || {};
    for (const step of Object.values(ops)) {
      if (step?.user_id === uid) return step.full_name;
    }
    return "";
  })();

  // Iter134 — Sblocco pagamento offline (bonifico). Il tasto "Pagamento ricevuto"
  // è visibile SOLO per admin e accounting_operator. I supervisor NON possono
  // sbloccare i pagamenti (policy). Il pulsante appare solo quando la pratica
  // è effettivamente in `awaiting_payment`.
  const awaitingPayment = p.status === "awaiting_payment";
  const canMarkPaymentReceived = awaitingPayment && (
    user.role === "admin" || user.role === "accounting_operator"
  );
  const markPaymentReceived = async () => {
    if (!window.confirm("Confermi di aver ricevuto il pagamento? La pratica sarà attivata.")) return;
    try {
      await api.post(`/practices/${id}/mark-payment-received`);
      toast.success("Pagamento confermato: pratica attivata.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nella conferma del pagamento.");
    }
  };

  return (
    <div>
      <Link
        to={isPro ? "/dashboard" : "/board"}
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"
        data-testid="back-link"
      >
        <ArrowLeft size={16} /> <KanbanIcon size={14} /> {isPro ? "Torna alla dashboard" : "Torna alla bacheca"}
      </Link>

      {p.replaces_practice_id && (
        <div className="mb-3 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm text-indigo-900" data-testid="practice-replaces-banner">
          Questa pratica è collegata alla pratica precedente{" "}
          <Link to={`/practices/${p.replaces_practice_id}`} className="font-semibold underline" data-testid="practice-replaces-link">#{p.replaces_practice_id.slice(0, 8)}</Link>
        </div>
      )}
      {p.replaced_by_practice_id && (
        <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="practice-replaced-by-banner">
          {p.change_mode === "replace" ? "Questa pratica è stata sostituita da" : "Questa pratica è collegata alla nuova pratica"}{" "}
          <Link to={`/practices/${p.replaced_by_practice_id}`} className="font-semibold underline" data-testid="practice-replaced-by-link">#{p.replaced_by_practice_id.slice(0, 8)}</Link>
        </div>
      )}

      {/* 📇 Dati di Contatto (iter52): visibile a admin/supervisor + operatori
          (attuali o pre-assegnati agli step). Il PRO/professional owner NON lo
          vede (i suoi contatti sono su Profilo). Posizionato in alto per
          consultazione rapida da parte degli operatori. */}
      <ContactInfoCard
        contactInfo={p.contact_info}
        canView={
          isAdmin ||
          isAssignee ||
          (Object.values(p.step_assignments || {}).includes(user.id))
        }
      />

      <div className={`grid grid-cols-1 gap-6 ${isPro ? "" : "lg:grid-cols-3"}`}>
        {/* Left: Practice info + workflow */}
        <div className={`space-y-4 ${isPro ? "" : "lg:col-span-2"}`}>
          <div className="kpi-card">
            <div className="flex items-start justify-between">
              <div>
                {/* Iter208.21 — Stato pratica: per "cancelled" mostra "ANNULLATA"
                    in rosso bold ben visibile; per gli altri stati usa lo stile eyebrow default. */}
                {p.status === "cancelled" ? (
                  <div
                    className="inline-flex items-center gap-1 text-xs font-black uppercase tracking-widest text-red-700 bg-red-100 border border-red-300 px-2.5 py-1 rounded-md shadow-sm"
                    data-testid="practice-status-cancelled"
                  >
                    <X size={12} weight="bold" />
                    ANNULLATA
                  </div>
                ) : (
                  <div className="label-eyebrow">{p.status}</div>
                )}
                <h1 className="font-display text-3xl font-bold tracking-tight mt-1">{p.service_name}</h1>
                {p.status === "cancelled" && (p.cancelled_reason || "").trim() && (
                  <div
                    className="mt-3 flex items-start gap-2 rounded-lg border-l-4 border-red-500 bg-red-50 px-4 py-3"
                    data-testid="practice-cancelled-reason"
                  >
                    <Warning size={18} weight="bold" className="text-red-600 mt-0.5 shrink-0" />
                    <div>
                      <div className="text-[11px] font-bold uppercase tracking-wider text-red-700">Motivazione annullamento</div>
                      <div className="text-sm text-red-900 whitespace-pre-line mt-0.5" data-testid="practice-cancelled-reason-text">{p.cancelled_reason}</div>
                    </div>
                  </div>
                )}
                {purchasedChained.length > 0 && (
                  <div
                    className="mt-1 flex flex-wrap items-center gap-1.5"
                    data-testid="chained-services-summary"
                  >
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      + Accessori:
                    </span>
                    {purchasedChained.map((cs, i) => (
                      <span
                        key={cs.service_id || cs.name || i}
                        className="inline-flex items-center gap-1 rounded-full bg-purple-50 border border-purple-200 px-2 py-0.5 text-xs font-medium text-purple-800"
                        data-testid={`chained-service-chip-${i}`}
                      >
                        {cs.name || "Servizio accessorio"}
                      </span>
                    ))}
                  </div>
                )}
                {(() => {
                  const parts = [];
                  if (prop) {
                    let line = prop.address || "";
                    if (prop.city) line += `, ${prop.city}`;
                    if (prop.province) line += ` (${prop.province})`;
                    if (prop.zip_code) line += ` — ${prop.zip_code}`;
                    parts.push(line);
                  } else if (p.property_address) {
                    parts.push(p.property_address);
                  }
                  const owner = prop ? (prop.owner_type === "azienda" ? (prop.owner_company_name || "") : `${prop.owner_first_name || ""} ${prop.owner_last_name || ""}`.trim()) : "";
                  return (
                    <>
                      {parts.length > 0 && (
                        <div className="flex items-center gap-1 text-sm text-muted-foreground mt-2">
                          <MapPin size={14} /> {parts.join(" · ")}
                        </div>
                      )}
                      {owner && (
                        <div className="flex items-center gap-1 text-sm text-slate-700 mt-1">
                          <User size={14} /> {owner}
                        </div>
                      )}
                      {/* 📅 Sopralluogo fissato: visibile a tutti gli operatori */}
                      {p.scheduled_appointment && (
                        <div
                          className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-blue-50 border border-blue-200 px-2.5 py-1 text-xs font-semibold text-blue-800"
                          data-testid="scheduled-appointment-header"
                        >
                          <Clock size={13} weight="fill" />
                          Sopralluogo:{" "}
                          {new Date(String(p.scheduled_appointment).replace(" ", "T")).toLocaleString("it-IT", {
                            weekday: "short", day: "2-digit", month: "2-digit", year: "numeric",
                            hour: "2-digit", minute: "2-digit",
                          })}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
              {(user.role === "admin" || user.role === "professional") && (
                <div className="text-right">
                  <div className="font-display text-2xl font-bold text-purple-700">{fmt(p.total_price)}</div>
                  {p.change_mode === "add_sum" && p.linked_practice_ids?.length > 0 && p.linked_group_total != null && (
                    <div className="mt-1 inline-block rounded-md bg-indigo-50 border border-indigo-200 px-2 py-0.5 text-xs font-semibold text-indigo-800" data-testid="practice-linked-group-total">
                      Totale di gruppo: {fmt(p.linked_group_total)}
                    </div>
                  )}
                  {(() => {
                    const statusMap = {
                      unpaid: { label: "Non pagato", cls: "text-slate-500" },
                      via_plafond: { label: "Via plafond", cls: "text-purple-700" },
                      authorized: { label: "🔒 Autorizzato (addebito alla consegna)", cls: "text-amber-700" },
                      requires_action: { label: "⚠️ Serve conferma cliente (SCA)", cls: "text-red-700" },
                      paid: { label: "✓ Pagato", cls: "text-emerald-700" },
                      released: { label: "Autorizzazione rilasciata", cls: "text-slate-500" },
                      refunded: { label: "Rimborsato", cls: "text-slate-500" },
                      cancelled: { label: "Annullato", cls: "text-slate-500" },
                    };
                    const s = statusMap[p.payment_status] || { label: p.payment_status, cls: "text-slate-500" };
                    // Iter140b: mostra il nome REALE del metodo di pagamento
                    // (es. "Bonifico", "Stripe"). Priorità: name → type → legacy.
                    const methodLabel = (
                      p.payment_method_name ||
                      p.payment_method_type ||
                      p.payment_method ||
                      ""
                    ).toString().toUpperCase();
                    return (
                      <div className={`text-xs uppercase font-semibold ${s.cls}`}>
                        {methodLabel ? `${methodLabel} · ` : ""}{s.label}
                      </div>
                    );
                  })()}
                  {p.payment_status === "paid" && p.payment_type !== "plafond" && p.receipt_number && (
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/practices/${p._id}/receipt.pdf`}
                      target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-purple-700 hover:text-purple-900 underline"
                      data-testid={`download-receipt-${p._id}`}
                    >
                      📄 Ricevuta {p.receipt_number}
                    </a>
                  )}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-6 pt-4 border-t">
              <div><div className="label-eyebrow text-[10px]">Professionista</div><div className="text-sm font-medium mt-1" data-testid="practice-detail-professional-name">{p.professional_company_name || p.professional_name}</div></div>
              <div><div className="label-eyebrow text-[10px]">Priorità</div><div className="text-sm font-medium mt-1 capitalize">{p.priority}</div></div>
              <div><div className="label-eyebrow text-[10px]">Step corrente</div><div className="text-sm font-medium mt-1">{p.current_step_name || "—"}</div></div>
            </div>
            {/* Iter208.68 — Azioni custom configurate dall'admin per lo step corrente.
                Iter208.87 — Nascoste agli operatori/admin: visibili solo al Pro owner. */}
            {isPro && (
              <PracticeCustomActionsPanel
                practiceId={p._id}
                onSubmitted={() => load?.()}
              />
            )}
            {/* Badge informativo: sopralluogo precedente svolto da X (utile per PRO/desk
                che aprono la pratica e vogliono sapere chi conosce già l'immobile). */}
            {previousVisitOp && (
              <div
                className="mt-3 inline-flex items-center gap-2 rounded-full bg-blue-50 border border-blue-200 px-3 py-1 text-xs text-blue-900"
                title={`${previousVisitOp.first_name} ${previousVisitOp.last_name} ha già effettuato un sopralluogo su questo immobile. Al prossimo "Richiedi sopralluogo" potrai scegliere se affidarlo di nuovo a lui/lei.`}
                data-testid="previous-visit-op-badge"
              >
                <MapPin size={12} weight="fill" />
                <span>Sopralluogo già svolto da <b>{previousVisitOp.first_name} {previousVisitOp.last_name}</b></span>
              </div>
            )}
            {/* Iter134: Banner + tasto "Pagamento ricevuto" (admin/accounting) */}
            {awaitingPayment && (
              <div
                className="mt-3 flex flex-wrap items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2"
                data-testid="awaiting-payment-banner"
              >
                <span className="text-lg">💳</span>
                <div className="flex-1 text-xs text-amber-900">
                  <div className="font-semibold">Pagamento in attesa — {p.payment_method_name || "Bonifico"}</div>
                  {p.payment_method_instructions && (
                    <div className="mt-1 whitespace-pre-wrap text-amber-800">{p.payment_method_instructions}</div>
                  )}
                </div>
                {canMarkPaymentReceived ? (
                  <Button
                    onClick={markPaymentReceived}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white border-0"
                    data-testid="mark-payment-received-btn"
                  >
                    ✓ Pagamento ricevuto
                  </Button>
                ) : (
                  user.role === "admin" || user.is_supervisor || user.role === "accounting_operator" ? null : (
                    <span className="text-[11px] text-amber-800 italic">
                      Solo Admin/Accounting può sbloccare
                    </span>
                  )
                )}
              </div>
            )}
            {/* Iter124 + Iter156: Banner stato Revisione Supervisore. Ora
                include il nome dello step corrente per chiarezza (Iter145:
                revisione è per singolo step). */}
            {reviewEnabled && !isPro && (
              <div className="mt-3">
                {reviewCompleted ? (
                  <div
                    className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-xs text-emerald-900"
                    data-testid="review-status-completed"
                  >
                    ✓ <span>
                      Revisione dello step <strong>{currentStepSnapshot?.name || p.current_step_name}</strong> completata — consegna sbloccata
                    </span>
                  </div>
                ) : reviewRequested ? (
                  <div
                    className="inline-flex items-center gap-2 rounded-full bg-indigo-50 border border-indigo-200 px-3 py-1 text-xs text-indigo-900"
                    data-testid="review-status-requested"
                  >
                    🛡️ <span>
                      In attesa di revisione dello step <strong>{currentStepSnapshot?.name || p.current_step_name}</strong>
                      {assignedSupName ? ` da ${assignedSupName}` : ""} — avanzamento bloccato
                    </span>
                  </div>
                ) : (
                  <div
                    className="inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200 px-3 py-1 text-xs text-amber-900"
                    data-testid="review-status-pending"
                  >
                    ⚠️ <span>
                      Lo step <strong>{currentStepSnapshot?.name || p.current_step_name}</strong> richiede la Revisione Supervisore prima dell'avanzamento
                    </span>
                  </div>
                )}
              </div>
            )}
            {/* Barra azioni operatore/admin (nascosta ai PRO) */}
            <div className="mt-4 flex gap-2 flex-wrap">
              {/* "Richiedi integrazione documenti": visibile a operatori/admin (non al pro owner).
                  Nascosto per pratiche completate/annullate. */}
              {/* Visibilità allineata alla logica di autorizzazione backend:
                  admin/supervisor, assegnatario corrente o utente in step_assignments. */}
              {/* Richiedi Sopralluogo: visibile solo a Desk/Tech (o Admin) su pratiche attive
                  E solo se il servizio ha il flag `allow_secondary_visit` attivo.
                  Inserisce uno step site_visit + uno step di consegna finale
                  riassegnato al richiedente. */}
              {/* Iter104: Rilavora — Admin/Supervisor riapre pratica completata/archiviata */}
              {isAdmin && (p.status === "completed" || p.status === "archived") && (
                <Button
                  variant="outline"
                  className="border-purple-300 text-purple-700 hover:bg-purple-50"
                  onClick={() => setRilavoraOpen(true)}
                  data-testid="rilavora-btn"
                >
                  <ArrowsClockwise size={14} className="mr-2" weight="bold" /> Rilavora
                </Button>
              )}
              {/* Iter208.12 — Elimina definitivamente (Admin, solo pratiche annullate) */}
              {isAdmin && p.status === "cancelled" && (
                <Button
                  variant="outline"
                  className="border-red-400 text-red-700 hover:bg-red-50 hover:text-red-800"
                  onClick={() => setPermDeleteOpen(true)}
                  data-testid="permanent-delete-btn"
                >
                  <Trash size={14} className="mr-2" weight="bold" /> Elimina definitivamente
                </Button>
              )}
              {/* Archivia: solo admin/supervisor, solo pratiche completate o annullate non già archiviate. */}
              {isAdmin && !p.archived_without_delivery
                && (p.status === "completed" || p.status === "cancelled") && (
                <Button
                  variant="outline"
                  className="border-slate-300 text-slate-700 hover:bg-slate-50"
                  onClick={() => setArchiveOpen(true)}
                  data-testid="archive-without-delivery-btn"
                >
                  Archivia
                </Button>
              )}
              {p.status !== "completed" && p.status !== "cancelled"
                && (isAdmin || user.role === "desk_operator" || user.role === "tech_operator")
                && (
                  isAdmin
                  || p.current_assigned_to === user.id
                  || Object.values(p.step_assignments || {}).includes(user.id)
                )
                && svc.allow_secondary_visit && (
                <Button
                  variant="outline"
                  className="border-blue-300 text-blue-700 hover:bg-blue-50"
                  onClick={() => setSiteVisitReqOpen(true)}
                  data-testid="request-site-visit-btn"
                >
                  <MapPin size={14} className="mr-2" weight="bold" /> Richiedi sopralluogo
                </Button>
              )}
              {/* NB: "Cambia data" è una feature dei PRO nel marketplace
                  (RecapTable + PracticeQuickView). Non appare qui perché gli
                  operatori interni gestiscono i reschedule via calendario
                  operatore + admin. */}
              {/* Iter110: allineamento con Kanban — quando l'operatore
                  deve ancora accettare (stato "assegnazione"), mostra solo
                  Accetta + Revoca. */}
              {isAssegnazione && (
                <>
                  <Button
                    onClick={advance}
                    className="ht-gradient border-0"
                    data-testid="operator-accept-btn"
                  >
                    Accetta assegnazione <ArrowRight size={14} className="ml-2" />
                  </Button>
                  <Button
                    variant="outline"
                    className="border-red-300 text-red-700 hover:bg-red-50"
                    onClick={async () => {
                      const reason = window.prompt(
                        "Motivo del rifiuto (verrà comunicato all'admin):",
                        "",
                      );
                      if (reason === null) return;
                      const trimmed = (reason || "").trim();
                      if (trimmed.length < 3) {
                        toast.error("Inserisci un motivo di almeno 3 caratteri");
                        return;
                      }
                      try {
                        await api.post(`/practices/${id}/reject`, { reason: trimmed });
                        toast.success("Assegnazione revocata");
                        window.location.href = "/board";
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || "Errore");
                      }
                    }}
                    data-testid="operator-reject-btn"
                  >
                    <X size={14} className="mr-2" weight="bold" /> Revoca
                  </Button>
                </>
              )}
              <PracticeExtraActions
                p={p}
                user={user}
                isAdmin={isAdmin}
                isAssignee={isAssignee}
                myOperatorState={myOperatorState}
                onDone={load}
              />

              {p.status !== "completed" && p.status !== "cancelled"
                && user.role !== "professional"
                && p.professional_email
                && !isAssegnazione /* Iter110: nascondi se operator deve ancora Accettare */
                && (
                  isAdmin
                  || p.current_assigned_to === user.id
                  || Object.values(p.step_assignments || {}).includes(user.id)
                ) && (
                <Button
                  variant="outline"
                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                  onClick={() => setDocReqOpen(true)}
                  data-testid="request-documents-btn"
                >
                  <EnvelopeSimple size={14} className="mr-2" weight="bold" /> Richiedi integrazione documenti
                </Button>
              )}
              {canAdvance && p.status !== "completed" && p.status !== "cancelled" && (p.status !== "standby" || isAdmin) && !isAssegnazione && (
                <>
                  {/* Iter124: Revisione Supervisore */}
                  {reviewEnabled && canRequestReview && !reviewRequested && (
                    <Button
                      variant="outline"
                      className="border-indigo-400 text-indigo-700 hover:bg-indigo-50"
                      onClick={openReviewModal}
                      data-testid="request-review-btn"
                    >
                      🛡️ Richiedi Revisione
                    </Button>
                  )}
                  {reviewEnabled && isAssignedSupervisor && !reviewCompleted && (
                    <Button
                      className="bg-emerald-600 hover:bg-emerald-700 text-white border-0"
                      onClick={completeReview}
                      data-testid="complete-review-btn"
                    >
                      ✓ Completa verifica
                    </Button>
                  )}
                  {p.is_final_step && canOperate && (
                    <Button
                      onClick={() => setDeliverOpen(true)}
                      className="ht-gradient border-0"
                      disabled={deliverBlocked}
                      title={deliverBlocked ? "Consegna bloccata: attendi la revisione del supervisore" : undefined}
                      data-testid="deliver-btn"
                    >
                      <FileArrowUp size={14} className="mr-2" weight="bold" /> Consegna lavoro
                    </Button>
                  )}
                  {(!p.is_final_step || isAdmin) && (
                    <Button
                      onClick={advance}
                      className="ht-gradient border-0"
                      disabled={deliverBlocked && !isAdmin}
                      title={isAdmin ? "Avanzamento forzato: annulla eventuali blocchi e rimuove l'operatore corrente" : (deliverBlocked ? "Avanzamento bloccato: attendi la revisione del supervisore per questo step" : undefined)}
                      data-testid="advance-btn"
                    >
                      Avanza allo step successivo <ArrowRight size={14} className="ml-1" />
                    </Button>
                  )}
                  {isAdmin && (
                    <>
                      {p.status !== "cancelled" && (
                        <Button
                          variant="outline"
                          className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                          onClick={() => setChangeOpen(true)}
                          data-testid="practice-change-btn"
                        >
                          <ArrowsClockwise size={14} className="mr-2" weight="bold" /> Cambia pratica
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        className="border-purple-300 text-purple-700 hover:bg-purple-50"
                        onClick={() => setReassignOpen(true)}
                        data-testid="practice-reassign-btn"
                      >
                        <UserSwitch size={14} className="mr-2" /> Riassegna
                      </Button>
                      {p.status === "standby" && (
                        <Button
                          variant="outline"
                          className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          onClick={releaseStandby}
                          data-testid="practice-release-standby-btn"
                        >
                          <PlayCircle size={14} className="mr-2" /> Sblocca standby
                        </Button>
                      )}
                      {!["completed", "cancelled"].includes(p.status) && (
                        <Button
                          variant="outline"
                          className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                          onClick={() => setPriceEditOpen(true)}
                          data-testid="practice-price-edit-btn"
                        >
                          <CurrencyEur size={14} className="mr-2" /> Modifica prezzo
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        className="border-red-300 text-red-700 hover:bg-red-50"
                        onClick={() => setAdminRevokeOpen(true)}
                        data-testid="practice-revoke-btn"
                      >
                        <Trash size={14} className="mr-2" /> Annulla pratica
                      </Button>
                      {p.refund_status !== "refunded" && (
                        <Button
                          variant="outline"
                          className="border-amber-300 text-amber-700 hover:bg-amber-50"
                          onClick={() => {
                            setRefundMode("total");
                            setRefundAmount(String(Number(p.total_price || 0).toFixed(2)));
                            setRefundReason("");
                            setRefundOpen(true);
                          }}
                          data-testid="practice-refund-btn"
                        >
                          <CurrencyEur size={14} className="mr-2" /> Rimborsa
                        </Button>
                      )}
                      {p.refund_status === "refunded" && (
                        <span
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200"
                          data-testid="practice-refund-badge"
                          title={p.refund_reason || undefined}
                        >
                          <CurrencyEur size={12} weight="bold" /> Rimborsato € {Number(p.refund_amount || 0).toFixed(2)}
                        </span>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Widget valutazione: pratica completata + owner + non ancora votata */}
          {p.status === "completed" && p.professional_id === user.id && (
            <SatisfactionWidget
              practiceId={p._id || p.id}
              existingScore={p.satisfaction_score}
              onRated={(sc) => setData(prev => ({ ...prev, practice: { ...prev.practice, satisfaction_score: sc } }))}
            />
          )}

          {/* Iter159: rimossa la striscia orizzontale Workflow — l'informazione
              (nome step + ordine + past/current/upcoming) è ora dentro l'
              AssignmentFlowPanel come timeline verticale unificata. */}

          {/* Iter69/Iter89 — Note Interne (Istruzioni Admin): visibili solo
              se contengono testo; per gli Admin resta un'area cliccabile
              per aggiungerle anche se vuote. */}
          {isAdmin ? (
            <InternalNotesCard
              practiceId={p._id}
              initialValue={p.internal_notes || ""}
              onSaved={load}
            />
          ) : (!isPro && (
            <InternalNotesCard
              practiceId={p._id}
              initialValue={p.internal_notes || ""}
              readOnly
            />
          ))}

          {/* Pagamento diritti ente */}
          {(data.service?.has_entity_fees || p.entity_fee_requests?.length > 0) && (
            <EntityFeesPanel
              practice={p}
              user={user}
              onRefresh={load}
            />
          )}

          {(data.service?.has_signature_requests || p.signature_requests?.length > 0) && (
            <SignatureRequestsPanel
              requests={p.signature_requests || []}
              canSend={canSendSignature}
              onSend={() => setSignatureOpen(true)}
              practiceId={p._id}
              canCancel={canSendSignature}
              onRefresh={load}
            />
          )}


          {/* Iter63/64: Questionario cliente — visibile a tutti (PRO, operatori, admin).
              Se il servizio ha `questionnaire_fields`, mostra domanda per domanda
              con la relativa risposta. Altrimenti fallback al singolo campo iter63. */}
          {svc.has_questionnaire && (
            <div className="kpi-card p-4" data-testid="practice-questionnaire">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">❓</span>
                <h3 className="font-display text-lg font-semibold">Questionario cliente</h3>
              </div>
              {svc.question_text && (
                <p className="text-sm text-slate-700 whitespace-pre-line mb-3 italic">
                  {svc.question_text}
                </p>
              )}
              {(svc.questionnaire_fields && svc.questionnaire_fields.length > 0) ? (
                <div className="space-y-3">
                  {svc.questionnaire_fields.map((f) => {
                    const ans = (p.questionnaire_answers || {})[f.id];
                    const display = Array.isArray(ans)
                      ? (ans.length ? ans.join(", ") : null)
                      : (ans && String(ans).trim() ? String(ans) : null);
                    return (
                      <div key={f.id} className="border border-purple-100 rounded-md p-3 bg-purple-50/40">
                        <div className="text-[11px] uppercase tracking-wider font-semibold text-purple-700">
                          {f.label}
                        </div>
                        {display ? (
                          <p
                            className="text-sm text-slate-800 whitespace-pre-line mt-1"
                            data-testid={`practice-q-answer-${f.id}`}
                          >
                            {display}
                          </p>
                        ) : (
                          <p className="text-sm text-slate-400 italic mt-1">
                            Nessuna risposta.
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <>
                  <div className="text-[11px] uppercase tracking-wider font-semibold text-purple-700">Risposta del cliente</div>
                  {p.questionnaire_answer ? (
                    <p
                      className="text-sm text-slate-800 whitespace-pre-line mt-0.5 bg-purple-50 border border-purple-200 rounded-md p-3"
                      data-testid="practice-questionnaire-answer"
                    >
                      {p.questionnaire_answer}
                    </p>
                  ) : (
                    <p className="text-sm text-slate-400 italic mt-0.5">
                      Il cliente non ha ancora completato il pagamento e la risposta.
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {/* Iter117: Caratteristiche Servizio (Prezzo Flessibile) — visibile a
              tutti gli operatori assegnati + admin/supervisor. NON mostra i
              valori dei modifier (+20% / -10€) per privacy commerciale. */}
          {!isPro && Array.isArray(p.pricing_answers) && p.pricing_answers.length > 0 && (
            isAdmin
            || p.current_assigned_to === user.id
            || Object.values(p.step_assignments || {}).includes(user.id)
          ) && (
            <div className="mb-6 p-4 bg-white rounded-md border border-indigo-200" data-testid="practice-features-section">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">🧩</span>
                <h3 className="font-display text-lg font-semibold">Caratteristiche Servizio</h3>
              </div>
              <p className="text-xs text-slate-500 mb-3">
                Configurazione scelta dal cliente al momento dell'acquisto.
              </p>
              <div className="space-y-2">
                {p.pricing_answers.map((pa, idx) => (
                  <div
                    key={`${pa.question_id}-${idx}`}
                    className="flex items-start gap-2 p-2 rounded bg-indigo-50/60 border border-indigo-100"
                    data-testid={`practice-feature-${idx}`}
                  >
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-indigo-700 min-w-[24px] shrink-0 pt-0.5">
                      #{idx + 1}
                    </div>
                    <div className="flex-1">
                      <div className="text-xs font-semibold text-slate-700">
                        {pa.question_label || "Domanda"}
                      </div>
                      <div className="text-sm text-slate-900 font-medium">
                        {pa.answer_label || "—"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!isPro && p.status !== "completed" && (
            isAdmin
            || p.current_assigned_to === user.id
            || Object.values(p.step_assignments || {}).includes(user.id)
          ) && (
            <PropertyDocumentsSection
              practiceId={p._id}
              canView={true}
              variant="panel"
              defaultOpen={true}
            />
          )}

          <PersonalDocDeadlines propertyId={p.property_id} />

          {/* Documenti operativi — nascosti ai PRO (chat + docs interni operatori) */}
          {!isPro && (
          <div
            className="kpi-card"
            onDragOver={(e) => {
              e.preventDefault(); e.stopPropagation();
              e.currentTarget.classList.add("ring-2","ring-purple-400","bg-purple-50/40");
            }}
            onDragLeave={(e) => {
              e.preventDefault(); e.stopPropagation();
              e.currentTarget.classList.remove("ring-2","ring-purple-400","bg-purple-50/40");
            }}
            onDrop={(e) => {
              e.preventDefault(); e.stopPropagation();
              e.currentTarget.classList.remove("ring-2","ring-purple-400","bg-purple-50/40");
              const files = Array.from(e.dataTransfer?.files || []);
              if (!files.length) return;
              uploadDocs(files);
            }}
            data-testid="prac-docs-dropzone"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-lg font-semibold">Documenti operativi</h3>
              <input ref={fileRef} type="file" hidden multiple onChange={uploadDoc} data-testid="prac-doc-input" />
              <Button onClick={() => fileRef.current?.click()} size="sm" variant="outline" data-testid="upload-prac-doc">
                <UploadSimple size={14} className="mr-1" weight="bold" /> Carica
              </Button>
            </div>
            {data.documents.length === 0 ? (
              <div className="border-2 border-dashed border-purple-200 rounded-lg p-6 text-center">
                <UploadSimple size={22} className="mx-auto mb-2 text-purple-500" />
                <p className="text-sm text-muted-foreground">Trascina qui i file o clicca su "Carica"</p>
                <p className="text-[11px] text-muted-foreground mt-1">Puoi caricare più file insieme</p>
              </div>
            ) : (
              <PracticeDocsList docs={data.documents} practiceId={p._id} onReload={load} />
            )}
          </div>
          )}

          {/* Form di sopralluogo compilati — nascosti ai PRO */}
          {!isPro && (() => {
            const stepsWithForm = (p.workflow_steps_snapshot || []).filter(s => s.has_form && s.form_schema);
            const submitted = p.submitted_forms || {};
            if (stepsWithForm.length === 0) return null;
            return (
              <div className="kpi-card" data-testid="submitted-forms-viewer">
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="font-display text-lg font-semibold">Rilevamento sopralluogo</h3>
                  <span className="text-[10px] font-mono bg-purple-100 text-purple-700 rounded px-1.5 py-0.5">
                    {Object.keys(submitted).length}/{stepsWithForm.length} form
                  </span>
                </div>
                <div className="space-y-4">
                  {stepsWithForm.map(step => {
                    const submission = submitted[step.id];
                    const canCompile = (
                      p.current_step_id === step.id && (isAdmin || p.current_assigned_to === user.id)
                    );
                    if (submission) {
                      return (
                        <SubmittedFormView
                          key={step.id}
                          submission={submission}
                          canEdit={canCompile}
                          step={step}
                          practiceId={p._id}
                          onDone={load}
                        />
                      );
                    }
                    // Placeholder per form non ancora compilato — con pulsante "Compila form"
                    // se l'utente corrente è l'operatore assegnato allo step
                    const qCount = ((step.form_schema?.sections?.[0]?.questions) || []).length;
                    return (
                      <PendingFormRow
                        key={step.id}
                        step={step}
                        qCount={qCount}
                        canCompile={canCompile}
                        practiceId={p._id}
                        onDone={load}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Iter164 — Storico Sopralluoghi + Flusso Unificato spostati sotto
              Documenti operativi per rispecchiare l'ordine logico della pratica
              (dati → documenti → cronologia operativa → storico). */}
          {!isPro && <VisitHistoryTimeline practiceId={p._id} />}
          {!isPro && <AssignmentFlowPanel practice={p} currentUserId={user.id} />}

          {/* History timeline — nascosto ai PRO */}
          {!isPro && (
          <div className="kpi-card">
            <h3 className="font-display text-lg font-semibold mb-3">Storico</h3>
            <div className="space-y-2" data-testid="practice-history">
              {(p.history || []).slice().reverse().map((h, i) => {
                const { by, verb, target } = formatHistoryEntry(h);
                return (
                  <div key={`${h.at || i}-${h.action || ""}-${i}`} className="flex items-start gap-3 text-sm" data-testid={`practice-history-row-${i}`}>
                    <Clock size={14} className="text-muted-foreground mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <div className="text-slate-800">
                        <span className="font-semibold">{by}</span>
                        <span className="text-slate-600"> {verb}</span>
                        {target && <span className="text-slate-700">{target}</span>}
                      </div>
                      <div className="text-xs text-muted-foreground">{h.at ? fmtDate(h.at) : ""}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          )}
        </div>

        {/* Right: Chat — nascosto ai PRO (chat interna operatori) */}
        {!isPro && (
        <div className="lg:col-span-1">
          <div className="kpi-card flex flex-col h-[70vh] sticky top-20" data-testid="practice-chat">
            <div className="flex items-center justify-between mb-3 pb-3 border-b">
              <h3 className="font-display text-lg font-semibold">Chat pratica</h3>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{chatMessages.length} messaggi</span>
                {/* Iter97: badge visibile mentre la pagina carica; l'auto-mark
                    lo azzera automaticamente entro pochi ms. */}
                {unreadCount > 0 && (
                  <span
                    className="min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold flex items-center justify-center bg-red-500 text-white shadow-md shadow-red-500/40"
                    data-testid={`chat-unread-badge-${id}`}
                    title={`${unreadCount} notific${unreadCount === 1 ? "a" : "he"} non lett${unreadCount === 1 ? "a" : "e"}`}
                  >
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto space-y-3 pr-1">
              {chatMessages.length === 0 && (
                <div className="text-center text-sm text-muted-foreground py-8">Nessun messaggio ancora. Scrivi il primo!</div>
              )}
              {chatMessages.map(m => {
                const isMe = m.author_id === user.id;
                const mentionsMe = Array.isArray(m.mentioned_user_ids) && m.mentioned_user_ids.includes(user.id);
                const isSendBack = m.kind === "send_back";
                const isRilavora = m.kind === "rilavora"; // Iter104
                // Render body with @mentions highlighted
                const parts = [];
                const re = /@([A-Za-zÀ-ÿ0-9_\.\-]+)/g;
                let lastIdx = 0; let match; let k = 0;
                const body = messageDisplayText(m, [...mentionables, { id: user.id, display_name: `${user.first_name || ""} ${user.last_name || ""}`.trim() || undefined }]);
                while ((match = re.exec(body)) !== null) {
                  if (match.index > lastIdx) parts.push({ t: body.slice(lastIdx, match.index), k: k++ });
                  const token = match[1].toLowerCase();
                  const mu = mentionables.find(u => u.handle === token || (u.first_name || "").toLowerCase().replace(/\s+/g, "") === token);
                  const isMyTag = mu && mu.id === user.id;
                  parts.push({ mention: true, label: mu ? mu.display_name : match[0].slice(1), mine: isMyTag, k: k++ });
                  lastIdx = match.index + match[0].length;
                }
                if (lastIdx < body.length) parts.push({ t: body.slice(lastIdx), k: k++ });
                if (isRilavora) {
                  // Iter104: banner viola prominente per messaggi "Rilavora"
                  return (
                    <div key={m._id} className="flex justify-center" data-testid={`chat-msg-${m._id}`}>
                      <div className="w-full border-2 border-purple-400 bg-purple-50 rounded-lg p-3 shadow-sm">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-600 text-white text-[10px] font-bold uppercase tracking-widest">
                            🔄 Rilavora
                          </span>
                          <span className="text-xs font-semibold text-purple-800">Pratica riaperta per rilavorazione</span>
                          <span className="ml-auto text-[10px] text-purple-700/70">{fmtDate(m.created_at)}</span>
                        </div>
                        <div className="text-xs text-purple-800/80 mb-1.5">
                          <strong>{m.author_name}</strong> ha riaperto la pratica: correggi quanto indicato e riconsegna il lavoro.
                        </div>
                        <div className="text-sm text-slate-900 bg-white rounded border border-purple-200 p-2 whitespace-pre-wrap break-words">
                          {body}
                        </div>
                      </div>
                    </div>
                  );
                }
                if (isSendBack) {
                  // Rendering speciale: banner rosso full-width con titolo prominente per l'operatore precedente
                  return (
                    <div key={m._id} className="flex justify-center" data-testid={`chat-msg-${m._id}`}>
                      <div className="w-full border-2 border-red-400 bg-red-50 rounded-lg p-3 shadow-sm">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-600 text-white text-[10px] font-bold uppercase tracking-widest">
                            ⚠ Molto importante
                          </span>
                          <span className="text-xs font-semibold text-red-800">Pratica rimandata indietro</span>
                          <span className="ml-auto text-[10px] text-red-700/70">{fmtDate(m.created_at)}</span>
                        </div>
                        <div className="text-xs text-red-800/80 mb-1.5">
                          <strong>{m.author_name}</strong>
                          {m.from_step_name && (
                            <> ha respinto la pratica dallo step <strong>«{m.from_step_name}»</strong></>
                          )}
                          {m.to_step_name && (
                            <> → torna a <strong>«{m.to_step_name}»</strong></>
                          )}
                        </div>
                        <div className="text-sm text-slate-900 bg-white rounded border border-red-200 p-2 whitespace-pre-wrap break-words">
                          {body}
                        </div>
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={m._id} className={`flex ${isMe ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m._id}`}>
                    <div className={`max-w-[85%] ${isMe ? "" : ""}`}>
                      <div className="flex items-center gap-2 text-xs mb-1">
                        <User size={10} />
                        <span className="font-semibold" style={{ color: ROLE_COLORS[m.author_role] || "#666" }}>
                          {m.author_name}
                        </span>
                        <span className="text-muted-foreground">·</span>
                        <span className="text-muted-foreground">{fmtDate(m.created_at)}</span>
                        {mentionsMe && <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-semibold">@ ti ha menzionato</span>}
                      </div>
                      <div className={`px-3 py-2 rounded-lg text-sm leading-snug whitespace-pre-wrap break-words ${isMe ? "ht-gradient border-0 text-white" : mentionsMe ? "bg-amber-50 border border-amber-300" : "bg-[color:hsl(var(--surface-alt))] border"}`}>
                        {parts.map(pt => pt.mention ? (
                          <span key={pt.k} className={`inline-block px-1 rounded font-semibold ${pt.mine ? "bg-amber-200 text-amber-900" : (isMe ? "bg-white/25 text-white" : "bg-purple-100 text-purple-700")}`}>@{pt.label}</span>
                        ) : (
                          <span key={pt.k}>{pt.t}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={chatEndRef} />
            </div>

            <form onSubmit={send} className="mt-3 pt-3 border-t flex gap-2 relative">
              <div className="flex-1 relative">
                <Textarea ref={textareaRef} rows={2} placeholder="Scrivi un messaggio... (usa @ per menzionare)" value={msg}
                  onChange={onMsgChange}
                  onKeyDown={onMsgKeyDown}
                  data-testid="chat-input" />
                {mentionOpen && filteredMentions.length > 0 && (
                  <div className="absolute bottom-full left-0 mb-1 w-full max-w-xs bg-white border rounded-md shadow-lg z-30 overflow-hidden"
                    data-testid="mention-dropdown">
                    {filteredMentions.map((u, i) => (
                      <button key={u.id} type="button"
                        onMouseDown={(e) => { e.preventDefault(); insertMention(u); }}
                        className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between ${i === mentionIdx ? "bg-purple-50" : "hover:bg-gray-50"}`}
                        data-testid={`mention-opt-${u.handle}`}>
                        <div>
                          <div className="font-medium">{u.display_name}</div>
                          <div className="text-[10px] text-muted-foreground">@{u.handle}</div>
                        </div>
                        <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: (ROLE_COLORS[u.role] || "#666") + "22", color: ROLE_COLORS[u.role] || "#666" }}>
                          {u.role}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Button type="submit" disabled={sending || !msg.trim()} className="ht-gradient border-0" data-testid="chat-send">
                <PaperPlaneTilt size={16} weight="bold" />
              </Button>
              <VoiceRecorderButton
                disabled={sending}
                onTranscribed={(text) => {
                  const trimmed = text.trim();
                  if (!trimmed) return;
                  setMsg((prev) => {
                    const base = (prev || "").trim();
                    const joined = base ? `${base} ${trimmed}` : trimmed;
                    return joined;
                  });
                  if (textareaRef.current) {
                    textareaRef.current.focus();
                  }
                }}
              />
            </form>
          </div>
        </div>
        )}
      </div>

      {/* Azioni admin sulla pratica (visibili solo ad admin/supervisor).
          Le stesse dialog usate dalla Kanban card, così l'admin può riassegnare
          o annullare la pratica anche stando dentro il dettaglio. */}
      {isAdmin && (
        <>
          <PracticeChangeDialog practice={p} open={changeOpen} onClose={() => setChangeOpen(false)} onDone={load} />
          <ReassignDialog
            open={reassignOpen}
            onClose={() => setReassignOpen(false)}
            practiceId={p._id}
            onDone={load}
          />
          <PriceEditDialog
            open={priceEditOpen}
            onClose={() => setPriceEditOpen(false)}
            practice={p}
            onSaved={(changes) => setData((d) => ({ ...d, practice: { ...d.practice, ...changes } }))}
          />
          <AdminRevokeDialog
            open={adminRevokeOpen}
            onClose={() => setAdminRevokeOpen(false)}
            practiceId={p._id}
            serviceName={p.service_name}
            onDone={load}
          />
          {/* Iter208.48 — Rimborso Stripe/Bonifico */}
          <Dialog open={refundOpen} onOpenChange={setRefundOpen}>
            <DialogContent data-testid="refund-dialog">
              <DialogHeader>
                <DialogTitle>Rimborsa pratica</DialogTitle>
                <DialogDescription>
                  Totale pratica: € {Number(p.total_price || 0).toFixed(2)}. Il rimborso avverrà via
                  {" "}
                  <strong>{p.stripe_payment_intent_id && p.payment_status === "paid" ? "Stripe" : "bonifico manuale"}</strong>.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setRefundMode("total"); setRefundAmount(String(Number(p.total_price || 0).toFixed(2))); }}
                    className={`flex-1 px-3 py-2 rounded-md text-sm font-semibold border ${refundMode === "total" ? "bg-amber-600 border-amber-600 text-white" : "bg-white border-slate-300 text-slate-700"}`}
                    data-testid="refund-mode-total"
                  >
                    Totale
                  </button>
                  <button
                    type="button"
                    onClick={() => setRefundMode("partial")}
                    className={`flex-1 px-3 py-2 rounded-md text-sm font-semibold border ${refundMode === "partial" ? "bg-amber-600 border-amber-600 text-white" : "bg-white border-slate-300 text-slate-700"}`}
                    data-testid="refund-mode-partial"
                  >
                    Parziale
                  </button>
                </div>
                <label className="block text-xs font-semibold text-slate-700">
                  Importo (€)
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    max={Number(p.total_price || 0)}
                    value={refundAmount}
                    disabled={refundMode === "total"}
                    onChange={(e) => setRefundAmount(e.target.value)}
                    className="mt-1"
                    data-testid="refund-amount-input"
                  />
                </label>
                <label className="block text-xs font-semibold text-slate-700">
                  Motivazione
                  <Textarea
                    value={refundReason}
                    onChange={(e) => setRefundReason(e.target.value)}
                    placeholder="Es. Servizio non erogato, richiesta cliente…"
                    rows={3}
                    className="mt-1"
                    data-testid="refund-reason-input"
                  />
                </label>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setRefundOpen(false)} disabled={refunding} data-testid="refund-cancel">
                  Annulla
                </Button>
                <Button
                  className="bg-amber-600 hover:bg-amber-700 text-white border-0"
                  disabled={refunding || !refundAmount || Number(refundAmount) <= 0}
                  onClick={async () => {
                    setRefunding(true);
                    try {
                      const r = await api.post(`/practices/${p._id}/refund`, {
                        amount: Number(refundAmount),
                        reason: refundReason,
                      });
                      toast.success(`Rimborso registrato (${r.data.mode === "stripe" ? "Stripe" : "Bonifico"}) — € ${Number(r.data.amount).toFixed(2)}`);
                      setRefundOpen(false);
                      load();
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Errore rimborso");
                    } finally {
                      setRefunding(false);
                    }
                  }}
                  data-testid="refund-confirm"
                >
                  {refunding ? "Rimborso in corso…" : "Conferma rimborso"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}

      {/* Richiesta integrazione documenti — operatori/admin */}
      <RequestDocumentsDialog
        open={docReqOpen}
        onClose={() => setDocReqOpen(false)}
        practiceId={p._id}
        professionalEmail={p.professional_email}
        clientEmail={p.client_email}
        serviceName={p.service_name}
        onSent={() => load()}
      />

      {/* Iter113 — Invia documento da firmare (operatori/admin) */}
      <SendForSignatureDialog
        open={signatureOpen}
        onClose={() => setSignatureOpen(false)}
        practiceId={p._id}
        clientEmail={p.client_email}
        serviceName={p.service_name}
        onSent={() => load()}
      />

      {/* Modal visualizzazione richiesta integrazione documenti (per il PRO
          che ha ricevuto la richiesta e clicca sulla notifica). Titolo e
          descrizione dinamici con servizio + indirizzo immobile. */}
      <IntegrationRequestModal
        open={integrationRequestOpen}
        onClose={() => setIntegrationRequestOpen(false)}
        request={integrationRequestData}
        propertyId={p.property_id}
        serviceName={p.service_name}
        propertyAddress={(() => {
          if (prop) {
            const parts = [];
            if (prop.address) parts.push(prop.address);
            if (prop.city) parts.push(prop.city);
            return parts.join(", ");
          }
          return p.property_address || "";
        })()}
      />

      {/* Richiedi sopralluogo secondario — dialog completo con calendar + operator picker */}
      <RequestSiteVisitDialog
        open={siteVisitReqOpen}
        onClose={() => setSiteVisitReqOpen(false)}
        practice={p}
        onDone={() => load()}
      />

      {/* Iter104: Dialog "Rilavora" — Admin/Supervisor riapre pratica completata */}
      <Dialog open={rilavoraOpen} onOpenChange={(v) => {
        if (!v) { setRilavoraReason(""); setRilavoraConfirm(false); }
        setRilavoraOpen(v);
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-700">
              <ArrowsClockwise size={18} weight="fill" /> Rilavora pratica
            </DialogTitle>
            <DialogDescription>
              La pratica tornerà all'operatore precedentemente assegnato in stato
              "In lavorazione". Indica cosa deve correggere: la motivazione verrà
              aggiunta alle note interne e comparirà nella chat pratica.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">
                Motivazione (min. 10 caratteri) *
              </label>
              <Textarea
                rows={4}
                placeholder="Es. Ricontrolla i dati del catasto, il numero civico non corrisponde."
                value={rilavoraReason}
                onChange={(e) => setRilavoraReason(e.target.value)}
                data-testid="rilavora-reason-detail"
              />
              <div className={`text-[11px] mt-0.5 ${rilavoraReason.trim().length >= 10 ? "text-emerald-600" : "text-slate-400"}`}>
                {rilavoraReason.trim().length}/10 caratteri minimi
              </div>
            </div>
            <label className="flex items-start gap-2 text-xs cursor-pointer p-2 rounded border border-purple-200 bg-purple-50/50">
              <input
                type="checkbox"
                checked={rilavoraConfirm}
                onChange={(e) => setRilavoraConfirm(e.target.checked)}
                data-testid="rilavora-confirm-detail"
                className="mt-0.5"
              />
              <span className="text-slate-700">
                Confermo di voler <strong>riaprire</strong> questa pratica.
                L'operatore assegnato verrà notificato.
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setRilavoraOpen(false); setRilavoraReason(""); setRilavoraConfirm(false); }}>Annulla</Button>
            <Button
              onClick={doRilavora}
              disabled={rilavoraReason.trim().length < 10 || !rilavoraConfirm || rilavoraSubmitting}
              className="bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50"
              data-testid="confirm-rilavora-detail"
            >
              {rilavoraSubmitting ? "Invio…" : "Conferma rilavora"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter208.12 — Elimina definitivamente pratica annullata (Admin only) */}
      <Dialog open={permDeleteOpen} onOpenChange={(v) => { if (!permDeleting) { setPermDeleteOpen(v); if (!v) setPermDeleteConfirmText(""); } }}>
        <DialogContent className="max-w-md" data-testid="permanent-delete-modal">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <Trash size={20} weight="bold" /> Elimina definitivamente
            </DialogTitle>
            <DialogDescription>
              Sei sicuro? <strong>L'operazione è irreversibile</strong> e cancellerà la pratica
              e tutti i dati collegati (messaggi, documenti, form compilati, notifiche) dal database.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="p-3 rounded border border-red-200 bg-red-50 text-xs text-red-900">
              Digita <strong>ELIMINA</strong> nel campo qui sotto per confermare.
            </div>
            <Input
              value={permDeleteConfirmText}
              onChange={(e) => setPermDeleteConfirmText(e.target.value)}
              placeholder="ELIMINA"
              data-testid="permanent-delete-confirm-input"
              autoFocus
              disabled={permDeleting}
            />
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => { setPermDeleteOpen(false); setPermDeleteConfirmText(""); }}
              disabled={permDeleting}
              data-testid="permanent-delete-cancel"
            >
              Annulla
            </Button>
            <Button
              onClick={async () => {
                if (permDeleteConfirmText.trim() !== "ELIMINA") return;
                setPermDeleting(true);
                try {
                  await api.delete(`/practices/${id}/permanent`);
                  toast.success("Pratica eliminata definitivamente");
                  setPermDeleteOpen(false);
                  // Torna alla dashboard: la pratica non esiste più
                  window.location.href = "/board";
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Errore nell'eliminazione");
                } finally {
                  setPermDeleting(false);
                }
              }}
              disabled={permDeleteConfirmText.trim() !== "ELIMINA" || permDeleting}
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
              data-testid="permanent-delete-confirm-btn"
            >
              {permDeleting ? "Eliminazione…" : "Elimina definitivamente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter202 — Modal conferma "Archivia senza consegna" (solo pratiche rianimate) */}
      <Dialog open={archiveOpen} onOpenChange={(v) => { if (!archiveSubmitting) setArchiveOpen(v); }}>
        <DialogContent className="max-w-md" data-testid="archive-without-delivery-modal">
          <DialogHeader>
            <DialogTitle>Archivia senza consegna</DialogTitle>
            <DialogDescription>
              Archiviare questa pratica senza consegna? L'operazione non può essere annullata.
              La pratica passerà in stato <strong>completato</strong> senza generare compensazioni
              e senza upload di documenti finali.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setArchiveOpen(false)}
              disabled={archiveSubmitting}
              data-testid="archive-cancel-btn"
            >
              Annulla
            </Button>
            <Button
              onClick={doArchiveWithoutDelivery}
              disabled={archiveSubmitting}
              className="bg-slate-800 hover:bg-slate-900 text-white disabled:opacity-50"
              data-testid="archive-confirm-btn"
            >
              {archiveSubmitting ? "Archiviazione…" : "Archivia"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter124: Modal Richiedi Revisione Supervisore */}
      <Dialog open={reviewModalOpen} onOpenChange={(v) => { if (!reviewSubmitting) setReviewModalOpen(v); }}>
        <DialogContent className="max-w-md" data-testid="review-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              🛡️ Richiedi Revisione Supervisore
            </DialogTitle>
            <DialogDescription>
              Seleziona il supervisore che dovrà verificare il tuo lavoro prima della consegna finale. Riceverà una email e una notifica in app.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <label className="block text-sm font-medium text-slate-700">Supervisore</label>
            {supervisorLoading ? (
              <div className="text-xs text-slate-500">Carico l'elenco…</div>
            ) : supervisorsList.length === 0 ? (
              <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
                Nessun supervisore configurato. Contatta l'amministrazione.
              </div>
            ) : (
              <select
                className="w-full h-10 border rounded px-3 text-sm bg-white"
                value={pickedSupervisorId}
                onChange={(e) => setPickedSupervisorId(e.target.value)}
                data-testid="review-supervisor-select"
              >
                <option value="">— Seleziona un supervisore —</option>
                {supervisorsList.map((s) => (
                  <option key={s.id} value={s.id}>{s.name} · {s.email}</option>
                ))}
              </select>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReviewModalOpen(false)}
              disabled={reviewSubmitting}
              data-testid="review-cancel-btn"
            >
              Annulla
            </Button>
            <Button
              className="ht-gradient border-0"
              onClick={submitReviewRequest}
              disabled={!pickedSupervisorId || reviewSubmitting}
              data-testid="review-submit-btn"
            >
              {reviewSubmitting ? "Invio…" : "Invia richiesta"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog "Consegna lavoro" — upload PDF finali + doppia conferma. */}
      <Dialog open={deliverOpen} onOpenChange={(v) => { if (!delivering) setDeliverOpen(v); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileArrowUp size={18} weight="bold" />
              {deliverStep === 1 ? "Consegna lavoro finale" : "Conferma definitiva"}
            </DialogTitle>
            <DialogDescription>
              {deliverStep === 1
                ? "Carica i PDF finali del lavoro. Verranno archiviati nella cartella \"Consegna\" della pratica e la pratica sarà marcata come completata."
                : `Stai per completare la pratica. L'azione è irreversibile: i file saranno consegnati ${deliverSendTo === "client" ? "al cliente finale" : "al professionista"} e la pratica passerà nello storico.`}
            </DialogDescription>
          </DialogHeader>

          {deliverStep === 1 ? (
            <div className="space-y-3 py-2">
              <label
                htmlFor="deliver-files-input"
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.add("bg-purple-100","border-purple-500"); }}
                onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.remove("bg-purple-100","border-purple-500"); }}
                onDrop={(e) => {
                  e.preventDefault(); e.stopPropagation();
                  e.currentTarget.classList.remove("bg-purple-100","border-purple-500");
                  const allowedExts = [".pdf", ".zip", ".doc", ".docx", ".xls", ".xlsx", ".png", ".jpg", ".jpeg"];
                  const dropped = Array.from(e.dataTransfer?.files || [])
                    .filter(f => allowedExts.some(ext => f.name.toLowerCase().endsWith(ext)));
                  if (dropped.length === 0) { toast.error("Formato non ammesso. Accettati: PDF, ZIP, Office, Immagini"); return; }
                  // Merge con eventuali file già selezionati, deduplicando per nome+size.
                  const key = f => `${f.name}:${f.size}`;
                  const existing = new Set(deliverFiles.map(key));
                  const merged = [...deliverFiles, ...dropped.filter(f => !existing.has(key(f)))];
                  setDeliverFiles(merged);
                }}
                className="block border-2 border-dashed border-purple-300 rounded-lg p-6 text-center cursor-pointer hover:bg-purple-50 transition-colors"
                data-testid="deliver-dropzone"
              >
                <FileArrowUp size={28} className="mx-auto mb-2 text-purple-600" />
                <div className="text-sm font-medium">Trascina qui i file o clicca per selezionarli</div>
                <div className="text-xs text-muted-foreground mt-1">Formati ammessi: PDF, ZIP, Office, Immagini · file multipli consentiti</div>
                <input
                  id="deliver-files-input"
                  type="file"
                  multiple
                  accept=".pdf,.zip,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,application/pdf,application/zip,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/png,image/jpeg"
                  className="hidden"
                  onChange={(e) => setDeliverFiles(Array.from(e.target.files || []))}
                  data-testid="deliver-files-input"
                />
              </label>
              {deliverFiles.length > 0 && (
                <div className="border rounded-md divide-y">
                  {deliverFiles.map((f, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText size={14} className="shrink-0" />
                        <span className="truncate">{f.name}</span>
                        <span className="text-xs text-muted-foreground shrink-0">
                          {(f.size / 1024).toFixed(1)} KB
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setDeliverFiles(deliverFiles.filter((_, j) => j !== i))}
                        className="text-red-600 hover:bg-red-50 rounded p-1"
                        aria-label="Rimuovi file"
                      >
                        <Trash size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <RecipientPicker value={deliverSendTo} onChange={setDeliverSendTo} clientEmail={p.client_email} testIdPrefix="deliver-send-to" />
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded p-3">
              <div className="text-sm text-amber-900 font-medium mb-2">
                Stai per consegnare {deliverFiles.length} file:
              </div>
              <ul className="text-xs text-amber-800 space-y-0.5 list-disc list-inside">
                {deliverFiles.map((f, i) => (<li key={i}>{f.name}</li>))}
              </ul>
            </div>
          )}

          <DialogFooter className="gap-2">
            {deliverStep === 1 ? (
              <>
                <Button variant="outline" onClick={() => setDeliverOpen(false)} disabled={delivering} data-testid="deliver-cancel">
                  Annulla
                </Button>
                <Button
                  className="ht-gradient border-0 text-white"
                  onClick={() => {
                    if (!deliverFiles.length) { toast.error("Carica almeno un PDF"); return; }
                    setDeliverStep(2);
                  }}
                  disabled={delivering || !deliverFiles.length}
                  data-testid="deliver-confirm-1"
                >
                  Conferma consegna
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setDeliverStep(1)} disabled={delivering} data-testid="deliver-back">
                  Indietro
                </Button>
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700 text-white border-0"
                  onClick={doDeliver}
                  disabled={delivering}
                  data-testid="deliver-confirm-2"
                >
                  {delivering ? "Consegna in corso…" : "Sì, conferma definitivamente"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ==================== SignatureRequestsPanel ====================
const SIG_STATUS = {
  pending: { label: "In attesa", cls: "bg-slate-100 text-slate-700" },
  signed: { label: "Firmato", cls: "bg-emerald-100 text-emerald-800" },
  cancelled: { label: "Annullato", cls: "bg-red-100 text-red-800" },
};

function SignatureRequestsPanel({ requests, canSend, onSend, practiceId, canCancel, onRefresh }) {
  return (
    <div className="kpi-card" data-testid="signature-requests-panel" style={{ borderLeft: "4px solid #4F46E5" }}>
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <PenNib size={20} color="#4F46E5" weight="duotone" />
          <h3 className="font-display text-lg font-semibold">Documenti da firmare</h3>
          {requests.length > 0 && (
            <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800 font-semibold" data-testid="signature-requests-count">
              {requests.length} {requests.length === 1 ? "documento" : "documenti"}
            </span>
          )}
        </div>
        {canSend && (
          <Button size="sm" variant="outline" onClick={onSend} data-testid="send-for-signature-btn">
            + Invia documento
          </Button>
        )}
      </div>
      {requests.length === 0 ? (
        <p className="text-sm text-muted-foreground italic" data-testid="signature-requests-empty">Nessun documento inviato in firma.</p>
      ) : (
        <div className="space-y-2">
          {requests.map((r) => (
            <SignatureRequestRow key={r._id} request={r} practiceId={practiceId} canCancel={canCancel} onRefresh={onRefresh} />
          ))}
        </div>
      )}
    </div>
  );
}

function SignatureRequestRow({ request: r, practiceId, canCancel, onRefresh }) {
  const st = SIG_STATUS[r.status] || SIG_STATUS.pending;
  const cancel = async () => {
    if (!window.confirm("Annullare questa richiesta di firma?")) return;
    try {
      await api.patch(`/practices/${practiceId}/signature-requests/${r._id}/cancel`);
      toast.success("Richiesta di firma annullata");
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore annullamento");
    }
  };
  const isSigned = r.status === "signed";
  const fmt = (d) => (d ? new Date(d).toLocaleDateString("it-IT") : "—");
  return (
    <div
      className={`border rounded-lg p-3 ${isSigned ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-white"}`}
      data-testid={`signature-request-${r._id}`}
    >
      <div className="flex items-center gap-2 flex-wrap text-sm">
        <FileText size={14} className="text-indigo-700 shrink-0" />
        {r.public_url ? (
          <a href={r.public_url} target="_blank" rel="noopener noreferrer" className="font-semibold text-indigo-700 hover:underline truncate max-w-[260px]" data-testid={`signature-request-file-${r._id}`}>
            {r.original_filename}
          </a>
        ) : (
          <span className="font-semibold truncate max-w-[260px]">{r.original_filename}</span>
        )}
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${st.cls}`} data-testid={`signature-request-status-${r._id}`}>{st.label}</span>
        <span className="text-[11px] text-slate-500 ml-auto">Inviato il {fmt(r.created_at)}</span>
        {canCancel && r.status === "pending" && (
          <button
            type="button" onClick={cancel} title="Annulla richiesta di firma"
            className="p-1 rounded text-red-600 hover:bg-red-50"
            data-testid={`signature-cancel-btn-${r._id}`}
          >
            <Trash size={15} />
          </button>
        )}
      </div>
      <div className="flex items-center gap-3 flex-wrap mt-1 text-xs text-slate-600">
        <span data-testid={`signature-request-recipient-${r._id}`}>Destinatario: <strong>{r.recipient_name || "—"}</strong></span>
        {isSigned && <span className="text-emerald-700" data-testid={`signature-request-signed-at-${r._id}`}>Firmato il {fmt(r.signed_at)}</span>}
      </div>
    </div>
  );
}


// ==================== EntityFeesPanel ====================
function EntityFeesPanel({ practice, user, onRefresh }) {
  const isAdmin = user.role === "admin" || user.is_supervisor;
  const isCurrentAssignee = practice.current_assigned_to === user.id;
  // Admin/supervisor deve poter operare come qualunque operatore (upload bollettini,
  // conferma pagamenti esterni, ecc.) su tutte le pratiche.
  const isDeskCurrent = (practice.current_assigned_role === "desk_operator" && isCurrentAssignee) || isAdmin;
  const isProfOwner = practice.professional_id === user.id || !!practice.viewer_is_parent_pro;

  // Iter77: source of truth = `entity_fee_requests` (lista). Retro-compat: se
  // esiste solo il campo legacy `entity_fees`, lo wrappiamo in un elemento
  // della lista in fase di rendering (nessuna migrazione DB necessaria).
  const feeRequests = React.useMemo(() => {
    const list = Array.isArray(practice.entity_fee_requests) ? [...practice.entity_fee_requests] : [];
    const legacy = practice.entity_fees;
    if (legacy && list.length === 0) {
      list.push({ ...legacy, id: legacy.id || "__legacy__", __legacy: true });
    }
    return list;
  }, [practice.entity_fee_requests, practice.entity_fees]);

  // Upload state (desk)
  const [feeType, setFeeType] = useState("bollettino");
  const [amount, setAmount] = useState("");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showNewForm, setShowNewForm] = useState(feeRequests.length === 0);
  const [feeSendTo, setFeeSendTo] = useState("pro");
  const feeDest = feeSendTo === "client" ? "al cliente finale" : "al professionista";

  const submitFees = async () => {
    if (!files.length) return toast.error("Carica almeno un file");
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Inserisci un importo valido");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("fee_type", feeType);
      fd.append("amount", String(amt));
      fd.append("send_to", feeSendTo);
      files.forEach(f => fd.append("files", f));
      await api.post(`/practices/${practice._id}/entity-fees`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(feeType === "bollettino"
        ? `Bollettino inviato ${feeDest}`
        : `Richiesta di rimborso inviata ${feeDest}`);
      setFiles([]); setAmount("");
      // Non chiudo il form: l'utente potrebbe voler aggiungere subito un'altra richiesta.
      // Ma se ora c'è almeno una richiesta pendente, lo comprimo.
      setShowNewForm(false);
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore invio");
    } finally { setUploading(false); }
  };

  return (
    <div id="bollettini" className="kpi-card scroll-mt-24" data-testid="entity-fees-panel" style={{ borderLeft: "4px solid #6D28D9" }}>
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Bank size={20} color="#6D28D9" weight="duotone" />
          <h3 className="font-display text-lg font-semibold">Pagamento diritti ente</h3>
          {feeRequests.length > 0 && (
            <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 font-semibold">
              {feeRequests.length} {feeRequests.length === 1 ? "richiesta" : "richieste"}
            </span>
          )}
        </div>
        {isDeskCurrent && feeRequests.length > 0 && !showNewForm && (
          <Button
            size="sm" variant="outline"
            onClick={() => setShowNewForm(true)}
            data-testid="fee-add-new-btn"
          >
            + Nuova richiesta
          </Button>
        )}
      </div>

      {/* Elenco richieste esistenti (Iter77) */}
      {feeRequests.length > 0 && (
        <div className="space-y-2 mb-3">
          {feeRequests.map((req, idx) => (
            <FeeRequestRow
              key={req.id || `legacy-${idx}`}
              request={req}
              practice={practice}
              user={user}
              isProfOwner={isProfOwner}
              isDeskCurrent={isDeskCurrent}
              isAdmin={isAdmin}
              onRefresh={onRefresh}
            />
          ))}
        </div>
      )}

      {/* Form: mostra sempre per il desk (nascosto se già c'è una richiesta e non richiesto)
          Non-desk: vista informativa se nessuna richiesta */}
      {feeRequests.length === 0 && !isDeskCurrent && (
        <p className="text-sm text-muted-foreground italic">
          Questo servizio richiede il pagamento dei diritti ente. Il desk operator caricherà bollettino o ricevuta.
        </p>
      )}
      {isDeskCurrent && showNewForm && (
        <div className="space-y-3 border-t pt-3" data-testid="fee-new-request-form">
          <div className="text-xs font-semibold uppercase tracking-widest text-purple-800">
            {feeRequests.length === 0 ? "Prima richiesta" : "Nuova richiesta aggiuntiva"}
          </div>
          <p className="text-sm text-muted-foreground">
            {feeSendTo === "client"
              ? <>Carica il <strong>bollettino</strong> (il cliente finale pagherà all'ente) oppure la <strong>ricevuta</strong> (Housetag ha pagato — il cliente rimborserà via link).</>
              : <>Carica il <strong>bollettino</strong> (il professionista pagherà all'ente) oppure la <strong>ricevuta</strong> (Housetag ha pagato — il professionista rimborserà via link).</>}
          </p>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setFeeType("bollettino")}
              className={`p-3 border-2 rounded-lg text-left ${feeType === "bollettino" ? "border-purple-500 bg-purple-50" : "border-slate-200"}`}
              data-testid="fee-type-bollettino">
              <div className="font-semibold text-sm flex items-center gap-2"><Receipt size={16} /> Bollettino</div>
              <div className="text-xs text-muted-foreground mt-1">Pro paga direttamente all'ente</div>
            </button>
            <button type="button" onClick={() => setFeeType("ricevuta")}
              className={`p-3 border-2 rounded-lg text-left ${feeType === "ricevuta" ? "border-purple-500 bg-purple-50" : "border-slate-200"}`}
              data-testid="fee-type-ricevuta">
              <div className="font-semibold text-sm flex items-center gap-2"><CheckCircle size={16} /> Ricevuta</div>
              <div className="text-xs text-muted-foreground mt-1">Housetag ha pagato · rimborso</div>
            </button>
          </div>
          <div>
            <label className="text-xs font-medium">Importo diritti (€)</label>
            <Input type="number" step="0.01" min="0" value={amount} onChange={e => setAmount(e.target.value)}
              placeholder="es. 45,00" data-testid="fee-amount-input" />
          </div>
          <div>
            <label className="text-xs font-medium">File PDF ({feeType})</label>
            <input type="file" multiple accept=".pdf,application/pdf"
              onChange={e => setFiles(Array.from(e.target.files || []))}
              data-testid="fee-files-input"
              className="block w-full text-sm mt-1" />
            {files.length > 0 && (
              <div className="text-xs text-muted-foreground mt-1">{files.length} file selezionati</div>
            )}
          </div>
          <RecipientPicker value={feeSendTo} onChange={setFeeSendTo} clientEmail={practice.client_email} testIdPrefix="fee-send-to" />
          <div className="flex items-center gap-2">
            <Button onClick={submitFees} disabled={uploading} className="ht-gradient border-0" data-testid="fee-submit-btn">
              {uploading ? "Invio…" : (feeType === "bollettino"
                ? `Invia bollettino ${feeSendTo === "client" ? "al cliente" : "al pro"}`
                : `Invia richiesta rimborso ${feeSendTo === "client" ? "al cliente" : "al pro"}`)}
            </Button>
            {feeRequests.length > 0 && (
              <Button variant="outline" onClick={() => { setShowNewForm(false); setFiles([]); setAmount(""); }}>
                Annulla
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * FeeRequestRow (Iter77) — Singola richiesta diritti ente nella lista.
 * Ogni riga ha il proprio stato + azioni (confirm bollettino / paga rimborso).
 */
function FeeRequestRow({ request, practice, user, isProfOwner, isDeskCurrent, isAdmin, onRefresh }) {
  const [receiptFiles, setReceiptFiles] = useState([]);
  const [confirming, setConfirming] = useState(false);
  // Iter209cd — modal di scelta metodo di pagamento cliente-facing.
  const [pickerOpen, setPickerOpen] = useState(false);
  const isPaid = request.payment_status === "paid";
  const isCancelled = request.payment_status === "cancelled";
  const canCancel = (isAdmin || isDeskCurrent) && !isPaid && !isCancelled && request.id && !request.__legacy;

  const cancelRequest = async () => {
    if (!window.confirm("Annullare questa richiesta di pagamento?")) return;
    try {
      await api.patch(`/practices/${practice._id}/entity-fees/${request.id}/cancel`);
      toast.success("Richiesta di pagamento annullata");
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore annullamento");
    }
  };

  const confirmExternalPayment = async () => {
    if (!receiptFiles.length) {
      toast.error("Carica la ricevuta di pagamento prima di confermare");
      return;
    }
    setConfirming(true);
    try {
      const fd = new FormData();
      if (request.id && !request.__legacy) fd.append("request_id", request.id);
      receiptFiles.forEach(f => fd.append("files", f));
      await api.post(`/practices/${practice._id}/entity-fees/confirm`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Pagamento confermato. La ricevuta è stata inviata al desk.");
      setReceiptFiles([]);
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally {
      setConfirming(false);
    }
  };

  const startPayFlow = () => setPickerOpen(true);

  const proceedStripe = () => {
    const rid = request.id && !request.__legacy ? `&request_id=${encodeURIComponent(request.id)}` : "";
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/stripe/mock-success/${practice._id}?fees=1${rid}`;
    window.open(url, "_blank");
    setTimeout(onRefresh, 1500);
  };

  const proceedOffline = (method) => {
    // Bonifico / altri metodi offline: mostro le istruzioni in una toast
    // "sticky" così il pro può copiare gli IBAN. La conferma effettiva del
    // pagamento avviene tramite il flusso "carica ricevuta + Ho pagato".
    toast.info(
      method?.instructions
        ? `${method.name}\n\n${method.instructions}`
        : `Procedi con ${method?.name || "bonifico"}. Poi carica la ricevuta.`,
      { duration: 15000 },
    );
  };

  const header = (
    <div className="flex items-center gap-2 flex-wrap text-sm">
      <span className="font-semibold">{request.type === "bollettino" ? "Bollettino" : "Ricevuta"}</span>
      <span className="text-slate-500">·</span>
      <span className="font-mono">€ {Number(request.amount || 0).toFixed(2)}</span>
      <span className="text-slate-500">·</span>
      <span
        className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${isCancelled ? "bg-slate-200 text-slate-700" : isPaid ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}
        data-testid={`fee-status-${request.id || "legacy"}`}
      >
        {isCancelled ? "Annullata" : isPaid ? "✓ Pagato" : "⏳ In attesa"}
      </span>
      {request.uploaded_at && (
        <span className="text-[11px] text-slate-500 ml-auto">
          {new Date(request.uploaded_at).toLocaleDateString("it-IT")}
        </span>
      )}
      {canCancel && (
        <button
          type="button" onClick={cancelRequest} title="Annulla richiesta di pagamento"
          className="p-1 rounded text-red-600 hover:bg-red-50"
          data-testid={`fee-cancel-btn-${request.id}`}
        >
          <Trash size={15} />
        </button>
      )}
    </div>
  );

  if (isCancelled) {
    return (
      <div className="border rounded-lg p-3 border-slate-200 bg-slate-50 opacity-80" data-testid={`fee-request-${request.id}`}>
        {header}
        {request.cancelled_at && (
          <div className="text-xs text-slate-500 mt-1">Annullata il {new Date(request.cancelled_at).toLocaleDateString("it-IT")}</div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`border rounded-lg p-3 ${isPaid ? "border-emerald-200 bg-emerald-50/40" : "border-amber-200 bg-amber-50/40"}`}
      data-testid={`fee-request-${request.id || "legacy"}`}
    >
      {header}
      {isFeeLinkExpiredUnpaid(request) && (
        <div
          className="mt-2 flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800"
          data-testid={`fee-expired-warning-${request.id || "legacy"}`}
        >
          <Warning size={16} weight="fill" className="shrink-0 mt-0.5" />
          <span>
            <strong>Pratica a rischio annullamento per mancato pagamento.</strong> Sono trascorsi più di {FEE_LINK_TTL_DAYS} giorni
            dall'invio del bollettino e il link al documento è scaduto. Invia una nuova richiesta o sollecita il professionista.
          </span>
        </div>
      )}
      {request.files?.length > 0 && (
        <div className="space-y-1 mt-2">
          {request.files.map((f) => {
            const url = f.signed_url || f.url;
            const dlUrl = url + (url.includes("?") ? "&" : "?") + "download=1";
            return (
              <div key={f.url || f.filename} className="flex items-center gap-3 flex-wrap">
                <a href={url} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-purple-700 hover:underline">
                  <FileText size={14} /> {f.filename}
                </a>
                <a href={dlUrl} download={f.filename}
                  className="text-[11px] text-slate-500 hover:text-purple-700 underline"
                  data-testid={`fee-file-download-${f.filename}`}>
                  Scarica
                </a>
                {/* Iter140l: condivisione nativa mobile con signed URL fresco */}
                <button
                  type="button"
                  onClick={() => shareFile({ url, filename: f.filename, title: f.filename })}
                  className="text-[11px] text-slate-500 hover:text-sky-700 underline inline-flex items-center gap-1"
                  data-testid={`fee-file-share-${f.filename}`}
                >
                  <ShareNetwork size={12} weight="bold" /> Condividi
                </button>
              </div>
            );
          })}
        </div>
      )}
      {request.payment_receipt?.length > 0 && (
        <div className="mt-2 text-xs">
          <div className="text-[10px] uppercase tracking-widest text-emerald-800 font-semibold mb-1">
            Ricevuta di pagamento
          </div>
          <div className="space-y-1">
            {request.payment_receipt.map((f) => {
              const url = f.signed_url || f.url;
              const dlUrl = url + (url.includes("?") ? "&" : "?") + "download=1";
              return (
                <div key={f.url || f.filename} className="flex items-center gap-3 flex-wrap">
                  <a href={url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 text-emerald-700 hover:underline">
                    <FileText size={14} /> {f.filename}
                  </a>
                  <a href={dlUrl} download={f.filename}
                    className="text-[11px] text-slate-500 hover:text-emerald-700 underline"
                    data-testid={`fee-receipt-download-${f.filename}`}>
                    Scarica
                  </a>
                  {/* Iter140l: condivisione nativa mobile con signed URL fresco */}
                  <button
                    type="button"
                    onClick={() => shareFile({ url, filename: f.filename, title: f.filename })}
                    className="text-[11px] text-slate-500 hover:text-sky-700 underline inline-flex items-center gap-1"
                    data-testid={`fee-receipt-share-${f.filename}`}
                  >
                    <ShareNetwork size={12} weight="bold" /> Condividi
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {/* Azioni per il professionista sulla richiesta pendente */}
      {isProfOwner && !isPaid && (
        <div className="border-t border-slate-200/60 mt-3 pt-3 space-y-2">
          {request.type === "bollettino" ? (
            <>
              <p className="text-xs text-muted-foreground">
                Paga il bollettino all'ente, carica la ricevuta e poi conferma. Il desk verrà notificato via email.
              </p>
              <div>
                <label className="text-xs font-medium">Ricevuta di pagamento (PDF/immagine)</label>
                <input
                  type="file"
                  multiple
                  accept=".pdf,application/pdf,image/*"
                  onChange={(e) => setReceiptFiles(Array.from(e.target.files || []))}
                  className="block w-full text-sm mt-1"
                  data-testid={`fee-receipt-input-${request.id || "legacy"}`}
                />
                {receiptFiles.length > 0 && (
                  <div className="text-xs text-muted-foreground mt-1">
                    {receiptFiles.length} file selezionati
                  </div>
                )}
              </div>
              <Button
                onClick={confirmExternalPayment}
                disabled={confirming || receiptFiles.length === 0}
                className="ht-gradient border-0"
                data-testid={`fee-confirm-external-btn-${request.id || "legacy"}`}
              >
                <CheckCircle size={14} className="mr-2" weight="bold" />
                {confirming ? "Invio…" : "Ho pagato — conferma"}
              </Button>
            </>
          ) : (
            <>
              <p className="text-xs text-muted-foreground">
                Housetag ha anticipato €{Number(request.amount || 0).toFixed(2)}. Rimborsa tramite pagamento.
              </p>
              <Button onClick={startPayFlow} className="ht-gradient border-0" data-testid={`fee-pay-stripe-btn-${request.id || "legacy"}`}>
                <CreditCard size={14} className="mr-2" weight="bold" /> Paga € {Number(request.amount || 0).toFixed(2)}
              </Button>
              <PaymentMethodPickerModal
                open={pickerOpen}
                onClose={() => setPickerOpen(false)}
                amount={Number(request.amount || 0)}
                onStripe={proceedStripe}
                onOffline={proceedOffline}
                title="Rimborso diritti ente — scegli come pagare"
              />
            </>
          )}
        </div>
      )}
      {/* Info per il desk (blocco visivo) */}
      {(isDeskCurrent || isAdmin) && !isPaid && (
        <div className="border-t border-slate-200/60 mt-3 pt-3 text-xs bg-amber-50 border-amber-200 rounded p-2 text-amber-800">
          ⚠️ In attesa saldo {request.recipient_type === "client" ? "dal cliente finale" : "dal professionista"}.
        </div>
      )}
      {isPaid && request.paid_at && (
        <div className="border-t border-slate-200/60 mt-3 pt-2 text-xs text-emerald-700">
          Pagato il {new Date(request.paid_at).toLocaleString("it-IT")}
          {request.paid_via === "external" && " · confermato dal professionista"}
          {request.paid_via === "stripe" && " · via Stripe"}
        </div>
      )}
    </div>
  );
}


// ==================== PENDING FORM ROW (con pulsante Compila) ====================
function PendingFormRow({ step, qCount, canCompile, practiceId, onDone }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="border rounded-lg overflow-hidden bg-slate-50" data-testid={`pending-form-${step.id}`}>
        <div className="px-3 py-2 bg-slate-100 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-mono uppercase tracking-widest rounded px-1.5 py-0.5 bg-slate-300 text-slate-700">
              In attesa
            </span>
            <span className="text-sm font-semibold">{step.name}</span>
          </div>
          <span className="text-[11px] text-slate-500">{qCount} domande</span>
        </div>
        <div className="p-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs text-slate-500 italic flex-1 min-w-[200px]">
            {canCompile
              ? "Puoi compilare il form ora. Puoi anche salvare in bozza e riprendere in seguito."
              : "Il form verrà compilato dall'operatore durante il sopralluogo. Le risposte compariranno qui."}
          </div>
          {canCompile && (
            <Button
              size="sm"
              className="ht-gradient border-0 text-white"
              onClick={() => setOpen(true)}
              data-testid={`compile-form-btn-${step.id}`}
            >
              📋 Compila form
            </Button>
          )}
        </div>
      </div>
      {canCompile && (
        <FormFillerDialog
          open={open}
          onClose={() => setOpen(false)}
          practiceId={practiceId}
          stepId={step.id}
          stepName={step.name}
          schema={step.form_schema}
          initialAnswers={{}}
          onSubmitted={() => { setOpen(false); onDone && onDone(); }}
        />
      )}
    </>
  );
}



// ==================== SUBMITTED FORM VIEWER (read-only) ====================
function SubmittedFormView({ submission, canEdit, step, practiceId, onDone }) {
  const [editOpen, setEditOpen] = useState(false);
  const schema = submission.form_schema || {};
  const answers = submission.answers || {};
  const questions = (schema.sections?.[0]?.questions) || [];
  const isDraft = !submission.completed_at;

  // Iter171 — Fix React error #31 nella sezione "Rilevamento sopralluogo".
  // Quando una domanda ha `enable_photo=true`, la risposta è salvata come
  // `{ value, photos: [...] }` (stesso formato di FormFillerDialog). Se
  // proviamo a renderizzare quell'oggetto direttamente React lancia
  // "Objects are not valid as a React child". Facciamo l'unwrap PRIMA di
  // qualsiasi render, esattamente come in `FormFillerDialog.unwrapAnswer`.
  const unwrap = (raw) => {
    if (raw && typeof raw === "object" && !Array.isArray(raw) && ("value" in raw || "photos" in raw)) {
      return { value: raw.value, photos: Array.isArray(raw.photos) ? raw.photos : [] };
    }
    return { value: raw, photos: [] };
  };

  const renderPhotoGrid = (photos, keyPrefix) => {
    if (!photos || photos.length === 0) return null;
    return (
      <div
        className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-2"
        data-testid={`answer-photos-${keyPrefix}`}
      >
        {photos.map((ph, i) => (
          <a
            key={ph.id || i}
            href={ph.signed_url || ph.url}
            target="_blank"
            rel="noopener noreferrer"
            className="aspect-square rounded overflow-hidden border bg-white block hover:ring-2 hover:ring-purple-400"
            data-testid={`answer-photo-${keyPrefix}-${ph.id || i}`}
            title={ph.filename || `Foto ${i + 1}`}
          >
            <img
              src={ph.signed_url || ph.url}
              alt={ph.filename || `foto ${i + 1}`}
              className="w-full h-full object-cover"
            />
          </a>
        ))}
      </div>
    );
  };

  const renderAnswer = (q) => {
    const { value: v, photos } = unwrap(answers[q.id]);
    const photoBlock = renderPhotoGrid(photos, q.id);
    // "Non risposto" solo se ANCHE non ci sono foto allegate.
    if ((v == null || v === "") && (!photos || photos.length === 0)) {
      return <span className="text-slate-400 italic">— non risposto</span>;
    }
    if (q.type === "yes_no") return (
      <>
        <span className={`font-semibold ${v === "Sì" ? "text-emerald-700" : "text-red-700"}`}>
          {v == null || v === "" ? "—" : String(v)}
        </span>
        {photoBlock}
      </>
    );
    if (q.type === "single_choice") {
      const opt = (q.options || []).find(o => o.id === v);
      return (
        <>
          <span>{opt?.label || (v == null ? "—" : String(v))}</span>
          {photoBlock}
        </>
      );
    }
    if (q.type === "multiple_choice") {
      if (!Array.isArray(v)) return (<><span>{v == null ? "—" : String(v)}</span>{photoBlock}</>);
      const labels = v.map(id => (q.options || []).find(o => o.id === id)?.label || id);
      return (<><span>{labels.join(", ") || "—"}</span>{photoBlock}</>);
    }
    if (q.type === "date") return (<><span>{v == null ? "—" : String(v)}</span>{photoBlock}</>);
    if (q.type === "photo_upload") {
      // Formato tradizionale: value stesso è l'array di foto.
      const allPhotos = Array.isArray(v) ? v : photos;
      if (!allPhotos || allPhotos.length === 0) {
        return <span className="text-slate-400 italic">— nessuna foto</span>;
      }
      return renderPhotoGrid(allPhotos, q.id);
    }
    return (
      <>
        <span className="whitespace-pre-wrap">{v == null ? "—" : String(v)}</span>
        {photoBlock}
      </>
    );
  };

  return (
    <div className="border rounded-lg overflow-hidden" data-testid={`submitted-form-${submission.step_id}`}>
      <div className={`px-3 py-2 flex items-center justify-between ${isDraft ? "bg-amber-50 border-b border-amber-200" : "bg-emerald-50 border-b border-emerald-200"}`}>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] font-mono uppercase tracking-widest rounded px-1.5 py-0.5 ${isDraft ? "bg-amber-200 text-amber-900" : "bg-emerald-200 text-emerald-900"}`}>
            {isDraft ? "Bozza" : "Compilato"}
          </span>
          <span className="text-sm font-semibold">{submission.step_name}</span>
        </div>
        <div className="text-[11px] text-slate-600">
          {submission.operator_name}
          {submission.completed_at && (
            <span className="ml-2 text-slate-400">
              · {new Date(submission.completed_at).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
          {canEdit && (
            <button
              type="button"
              onClick={() => setEditOpen(true)}
              className="ml-3 text-[11px] font-semibold text-purple-700 hover:text-purple-900 underline"
              data-testid={`edit-form-btn-${submission.step_id}`}
            >
              {isDraft ? "Riprendi compilazione" : "Modifica"}
            </button>
          )}
        </div>
      </div>
      <div className="p-3 space-y-2 bg-white">
        {(() => {
          // Iter176 — Nuovo layout SubmittedFormView:
          //   1) Nascondi domande senza risposta (nessun value E nessuna foto)
          //   2) Raggruppa per categoria (fallback "Generale")
          //   3) Accordion chiuso di default, chevron a destra
          const answeredQuestions = questions.filter((q) => {
            const { value: v, photos: ph } = unwrap(answers[q.id]);
            const hasValue = !(v == null || v === "" || (Array.isArray(v) && v.length === 0));
            const hasPhotos = Array.isArray(ph) && ph.length > 0;
            return hasValue || hasPhotos;
          });
          if (answeredQuestions.length === 0) {
            return questions.length === 0
              ? <div className="text-xs text-muted-foreground italic">Form vuoto</div>
              : <div className="text-xs text-muted-foreground italic">Nessuna risposta compilata</div>;
          }
          // Ordine di prima apparizione della categoria
          const groups = [];
          const groupIdx = new Map();
          for (const q of answeredQuestions) {
            const cat = (q.category || "").trim() || "Generale";
            if (!groupIdx.has(cat)) {
              groupIdx.set(cat, groups.length);
              groups.push({ category: cat, items: [] });
            }
            groups[groupIdx.get(cat)].items.push(q);
          }
          return groups.map((g) => (
            <SubmittedCategoryAccordion
              key={g.category}
              category={g.category}
              items={g.items}
              questions={questions}
              renderAnswer={renderAnswer}
            />
          ));
        })()}
      </div>
      {canEdit && step && (
        <FormFillerDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          practiceId={practiceId}
          stepId={submission.step_id}
          stepName={submission.step_name}
          schema={submission.form_schema || step.form_schema}
          initialAnswers={submission.answers || {}}
          onSubmitted={() => { setEditOpen(false); onDone && onDone(); }}
        />
      )}
    </div>
  );
}


/**
 * Iter176 — Categoria accordion per SubmittedFormView.
 * Chevron a destra, chiuso di default. Design coerente con l'accordion di
 * FormFillerDialog: header viola chiaro con contatore, apertura fluida.
 */
function SubmittedCategoryAccordion({ category, items, questions, renderAnswer }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="border border-slate-200 rounded-lg overflow-hidden bg-white"
      data-testid={`submitted-cat-${category}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-purple-50 hover:bg-purple-100 border-b border-purple-100 transition-colors"
        data-testid={`submitted-cat-toggle-${category}`}
        aria-expanded={open}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-semibold text-purple-900 truncate">{category}</span>
          <span className="text-[10px] text-purple-700 bg-purple-100 border border-purple-200 rounded-full px-2 py-0.5">
            {items.length}
          </span>
        </div>
        {open
          ? <CaretDown size={14} weight="bold" className="text-purple-700" />
          : <CaretRight size={14} weight="bold" className="text-purple-700" />}
      </button>
      {open && (
        <div className="p-3 space-y-3">
          {items.map((q) => {
            const originalIdx = questions.findIndex((x) => x.id === q.id);
            return (
              <div key={q.id} className="text-sm">
                <div className="text-[11px] font-semibold text-slate-500 mb-0.5">
                  <span className="font-mono bg-slate-100 rounded px-1 mr-1">#{originalIdx + 1}</span>
                  {q.label}
                </div>
                <div className="pl-2 border-l-2 border-purple-200 text-slate-800">
                  {renderAnswer(q)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ==================== ASSIGNMENT FLOW PANEL ====================
// Mostra a colpo d'occhio chi lavorerà ciascuno step della pratica
// (es. "1/2 Site operator · Mario Rossi"). Utile per admin/supervisor
// e per gli operatori in coda che devono sapere chi taggare in chat.
// Iter209au — ROLE_IT allineato al ROLE_LABELS canonico di @/lib/api.
// `supervisor` non è un ruolo separato: è un flag `is_supervisor`,
// ma lo mantengo qui per compatibilità con codice legacy che usa
// `ROLE_IT[op.role || "supervisor"]`.
const ROLE_IT = {
  ...ROLE_LABELS,
  supervisor: "Supervisor",
};

function AssignmentFlowPanel({ practice, currentUserId }) {
  const snap = practice?.workflow_steps_snapshot || [];
  const ops = practice?.assigned_operators || {};
  // Iter159: la timeline verticale unificata (step + assegnazione) sostituisce
  // la vecchia striscia orizzontale Workflow. Calcoliamo l'ordine dello step
  // corrente per determinare Past / Current / Upcoming.
  const stepReviews = practice?.step_reviews || {};
  const [pickerStepId, setPickerStepId] = useState(null);
  const currentOrder = React.useMemo(() => {
    const cur = snap.find((s) => s?.id === practice?.current_step_id);
    return cur?.order ?? -1;
  }, [snap, practice?.current_step_id]);
  const [operators, setOperators] = useState([]);
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.supervisor;

  // Lazy-load operatori quando l'admin apre il picker per la prima volta
  React.useEffect(() => {
    if (!pickerStepId || operators.length > 0 || !isAdmin) return;
    api.get("/users?operators_only=true").then((r) => {
      const arr = (r.data || []).filter((u) => u.status === "approved");
      setOperators(arr);
    }).catch(() => setOperators([]));
  }, [pickerStepId, operators.length, isAdmin]);

  if (snap.length === 0) return null;
  const steps = [...snap].sort((a, b) => (a.order || 0) - (b.order || 0));
  const total = steps.length;

  const assignTo = async (stepId, operatorId) => {
    setSaving(true);
    try {
      const r = await api.post(`/practices/${practice._id}/reassign-step`, {
        step_id: stepId, operator_id: operatorId,
      });
      const n = r.data?.cascade_count || 1;
      toast.success(n > 1
        ? `Assegnato — cascata su ${n} step collegati`
        : "Operatore assegnato");
      setPickerStepId(null);
      // Ricarica la pratica dal parent
      window.dispatchEvent(new CustomEvent("practice:reload"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore assegnazione");
    } finally {
      setSaving(false);
    }
  };

  // Compat: il parent PracticeDetail ascolta un evento `practice:reload`
  // e ricarica automaticamente (vedi useEffect di PracticeDetail).

  return (
    <div className="kpi-card" data-testid="assignment-flow-panel">
      <div className="label-eyebrow mb-3">Flusso di assegnazione</div>
      <div className="space-y-1.5">
        {steps.map((s, i) => {
          const op = ops[s.id];
          const isCurrent = practice.current_step_id === s.id;
          const isReturnStep = !!s.created_by_visit_request;
          const returnsToMe = isReturnStep && s.return_to_operator_id === currentUserId;
          const isPast = practice?.status === "completed" || (currentOrder >= 0 && (s.order ?? 0) < currentOrder);
          const isUpcoming = !isCurrent && !isPast;
          const isPickerOpen = pickerStepId === s.id;
          // Iter159: pallino status a sinistra (Past ✓ verde / Current ● viola pulse / Upcoming ○ grigio).
          const statusDot = isPast
            ? <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500 text-white text-[10px] shrink-0" title="Completato">✓</span>
            : isCurrent
              ? <span className="inline-block w-3 h-3 rounded-full bg-purple-600 shrink-0 animate-pulse" title="In corso" />
              : <span className="inline-block w-3 h-3 rounded-full border-2 border-slate-300 shrink-0" title="In attesa" />;
          return (
            <div
              key={s.id}
              data-testid={`assignment-flow-row-${s.id}`}
              className={`flex items-center gap-3 py-2.5 px-3 rounded-md border ${
                isCurrent ? "border-purple-300 bg-purple-50"
                  : isPast ? "border-emerald-100 bg-emerald-50/40"
                  : returnsToMe ? "border-blue-200 bg-blue-50/50"
                  : "border-slate-100 bg-white"
              } ${isUpcoming ? "opacity-70" : ""}`}
            >
              {statusDot}
              <div className="text-[11px] font-mono font-bold text-slate-500 shrink-0 w-10">
                {i + 1}/{total}
              </div>
              {/* Iter159: nome dello step + ruolo, stack verticale a sinistra */}
              <div className="min-w-[180px] shrink-0">
                <div className="text-sm font-semibold text-slate-900 truncate" data-testid={`assignment-flow-step-name-${s.id}`}>
                  {s.name || "—"}
                </div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
                  {ROLE_IT[s.assigned_role] || s.assigned_role || "—"}
                </div>
              </div>
              <div className="text-sm text-slate-900 flex-1 truncate flex items-center gap-2 min-w-0">
                <span className="truncate">
                  {op ? op.full_name : (
                    <span className="italic text-slate-400" data-testid={`assignment-flow-empty-${s.id}`}>
                      — nessun operatore assegnato
                    </span>
                  )}
                </span>
                {isReturnStep && (
                  returnsToMe ? (
                    <span
                      className="text-[10px] font-semibold uppercase tracking-wider bg-blue-600 text-white px-1.5 py-0.5 rounded shrink-0"
                      title="Al termine del sopralluogo la pratica ti tornerà per la consegna"
                      data-testid={`assignment-flow-returns-to-me-${s.id}`}
                    >
                      ↩ Ritorna a te
                    </span>
                  ) : (
                    <span
                      className="text-[10px] font-medium uppercase tracking-wider bg-blue-100 text-blue-800 border border-blue-200 px-1.5 py-0.5 rounded shrink-0"
                      title="Step di consegna aggiunto dopo un sopralluogo secondario"
                      data-testid={`assignment-flow-return-step-${s.id}`}
                    >
                      ↩ Post-sopralluogo
                    </span>
                  )
                )}
                {/* Iter156: badge Revisione Supervisore per singolo step */}
                {s.requires_supervision && (() => {
                  const review = stepReviews[s.id] || {};
                  if (review.completed) {
                    return (
                      <span
                        className="text-[10px] font-semibold uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-200 px-1.5 py-0.5 rounded shrink-0"
                        title="Revisione supervisore completata"
                        data-testid={`step-review-badge-${s.id}`}
                      >
                        ✅ Rev. OK
                      </span>
                    );
                  }
                  if (review.requested) {
                    return (
                      <span
                        className="text-[10px] font-semibold uppercase tracking-wider bg-amber-100 text-amber-900 border border-amber-300 px-1.5 py-0.5 rounded shrink-0 animate-pulse"
                        title="Revisione supervisore in corso — attesa completamento"
                        data-testid={`step-review-badge-${s.id}`}
                      >
                        🛡️ Rev. in corso
                      </span>
                    );
                  }
                  return (
                    <span
                      className="text-[10px] font-medium uppercase tracking-wider bg-slate-100 text-slate-600 border border-slate-200 px-1.5 py-0.5 rounded shrink-0"
                      title="Questo step richiede la revisione del supervisore prima dell'avanzamento"
                      data-testid={`step-review-badge-${s.id}`}
                    >
                      🛡️ Rev. richiesta
                    </span>
                  );
                })()}
              </div>
              {/* Iter149: tasto Assegna sui rows unassigned (solo admin/supervisor) */}
              {!op && isAdmin && !isPickerOpen && (
                <button
                  type="button"
                  onClick={() => setPickerStepId(s.id)}
                  className="text-[11px] font-semibold uppercase tracking-wider bg-slate-900 text-white px-2 py-1 rounded hover:bg-slate-700 transition shrink-0"
                  data-testid={`assignment-flow-assign-btn-${s.id}`}
                  disabled={saving}
                >
                  Assegna
                </button>
              )}
              {isCurrent && (
                <span className="text-[9px] font-mono uppercase tracking-widest bg-purple-600 text-white px-1.5 py-0.5 rounded shrink-0">
                  In corso
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Picker inline: appare sotto la lista quando un tasto "Assegna" è cliccato */}
      {pickerStepId && isAdmin && (
        <div
          className="mt-3 p-3 rounded-md border border-slate-200 bg-slate-50 space-y-2"
          data-testid="assignment-flow-picker"
        >
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold text-slate-700">Scegli operatore</div>
            <button
              type="button"
              onClick={() => setPickerStepId(null)}
              className="text-[11px] text-slate-500 hover:text-slate-800"
              data-testid="assignment-flow-picker-close"
            >
              Annulla
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => assignTo(pickerStepId, user.id)}
              disabled={saving}
              className="text-xs font-semibold bg-purple-600 text-white px-3 py-1.5 rounded hover:bg-purple-700 transition disabled:opacity-50"
              data-testid="assignment-flow-picker-assign-to-me"
            >
              👤 Assegna a me
            </button>
            {operators.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => assignTo(pickerStepId, o.id)}
                disabled={saving}
                className="text-xs bg-white border border-slate-300 text-slate-800 px-3 py-1.5 rounded hover:bg-slate-100 transition disabled:opacity-50"
                data-testid={`assignment-flow-picker-op-${o.id}`}
              >
                {o.first_name} {o.last_name}
                <span className="ml-1 text-[10px] text-slate-500">
                  {ROLE_IT[o.role] || o.role}
                </span>
              </button>
            ))}
            {operators.length === 0 && (
              <span className="text-xs text-slate-500 italic">Caricamento operatori…</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}



// ==================== PRACTICE DOCS LIST (drag&drop + delete + preview inline) ====================
// Tutti gli operatori assegnati (attuali/futuri) + admin + owner possono
// riordinare (drag&drop) e cancellare i documenti operativi della pratica.
function PracticeDocsList({ docs, practiceId, onReload }) {
  const [items, setItems] = useState(docs);
  const [dragId, setDragId] = useState(null);
  const [previewIdx, setPreviewIdx] = useState(null);

  React.useEffect(() => { setItems(docs); }, [docs]);

  const onDragStart = (id) => setDragId(id);
  const onDragOver = (e, targetId) => {
    e.preventDefault();
    if (!dragId || dragId === targetId) return;
    const from = items.findIndex(x => x._id === dragId);
    const to = items.findIndex(x => x._id === targetId);
    if (from < 0 || to < 0) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setItems(next);
  };
  const onDragEnd = async () => {
    if (!dragId) return;
    setDragId(null);
    try {
      await api.post(`/practices/${practiceId}/documents/reorder`, {
        doc_ids: items.map(x => x._id),
      });
      toast.success("Ordine aggiornato");
      onReload && onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore riordino");
      onReload && onReload();
    }
  };

  const doDelete = async (id, filename) => {
    if (!window.confirm(`Elimini "${filename}"? L'operazione è irreversibile.`)) return;
    try {
      await api.delete(`/practices/${practiceId}/documents/${id}`);
      toast.success("Documento eliminato");
      onReload && onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore eliminazione");
    }
  };

  return (
    <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1" data-testid="practice-docs-list">
      {items.map((d, i) => (
        <div
          key={d._id}
          draggable
          onDragStart={() => onDragStart(d._id)}
          onDragOver={(e) => onDragOver(e, d._id)}
          onDragEnd={onDragEnd}
          onDrop={onDragEnd}
          className={`flex items-center justify-between p-2 border rounded-md bg-white hover:bg-purple-50/40 transition ${dragId === d._id ? "opacity-50" : ""}`}
          data-testid={`practice-doc-${d._id}`}
        >
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <span className="cursor-grab text-slate-400 select-none" title="Trascina per riordinare">⋮⋮</span>
            <FileText size={18} color="#6D28D9" weight="duotone" className="shrink-0" />
            <div className="min-w-0 flex-1">
              <button
                type="button"
                onClick={() => setPreviewIdx(i)}
                className="text-sm font-medium text-slate-900 hover:text-purple-700 hover:underline text-left truncate block w-full"
                data-testid={`preview-doc-${d._id}`}
                title="Apri anteprima"
              >
                {d.filename}
              </button>
              <div className="text-xs text-muted-foreground truncate">
                {(d.file_size / 1024).toFixed(1)} KB · {new Date(d.created_at).toLocaleDateString("it-IT")}
                {d.uploader_name && ` · ${d.uploader_name}`}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={() => setPreviewIdx(i)}
              className="text-[11px] px-2 py-1 rounded text-purple-700 hover:bg-purple-100 font-semibold"
              data-testid={`open-doc-${d._id}`}
              title="Anteprima"
            >
              Anteprima
            </button>
            <button
              type="button"
              onClick={() => doDelete(d._id, d.filename)}
              className="text-[11px] px-2 py-1 rounded text-red-600 hover:bg-red-50 font-semibold"
              data-testid={`delete-doc-${d._id}`}
              title="Elimina"
            >
              🗑
            </button>
          </div>
        </div>
      ))}
      <FilePreviewModal
        docs={items}
        index={previewIdx}
        onIndexChange={setPreviewIdx}
        onClose={() => setPreviewIdx(null)}
      />
    </div>
  );
}


// ============================================================
// SatisfactionWidget estratto in /components/SatisfactionWidget.jsx per essere
// riusato anche dal modal marketplace `PracticeQuickView`.

